// The editor's timeline as one pure reducer (spec E2, the version 2 model): tracks, top to bottom,
// of clips placed in time, as live's editor keeps them. A video track holds videos and stills, an
// audio track sounds, a text track titles, an effect track effects (#265: an adjustment layer over
// the tracks below); the top track draws in front. Two pictures that meet on a video track make a
// cut, which may carry a transition (#240). Every edit goes through
// `editTimeline`, which keeps the bounds (a clip inside its source, never over another clip of its
// track, within the contract's counts; a transition on a cut, no longer than its clips), so a
// screen never writes a draft the server would refuse for its shape. An edit it cannot make it
// refuses with its reason, one of `REFUSALS` (`refusal`, #601), giving back the very same
// timeline. Times are whole milliseconds.

import type { EditAspect, EffectKind, SpeedCurveName, TransitionKind } from "../../api/generated/model";
import { BACKGROUND_KEY, MAX_PLACE, PLAIN_LOOK, colourOr, finite, looked, type Look } from "./look";
import { styled, type TextStylePatch, type TextStyle } from "./text-style";
import type { EffectValue } from "./effect-filters";
import { MAX_ANIMATIONS, PRESETS, type Animation } from "./animations";
import {
  MAX_SPEED,
  MIN_SPEED,
  clipMsAt,
  curveOver,
  curved,
  fileMsAt,
  heldSpeedPoints,
  lengthFactor,
  pointsOver,
  type CurvePoint,
  type SpeedPoint,
} from "./speed-curve";
import { DEFAULT_CAPTIONS, type CaptionSettings } from "./captions/caption-style";
import { segmentWords, type CaptionMetrics } from "./captions/segment";
import {
  MAX_CAPTIONS,
  MAX_TIMELINE_CAPTION_WORDS,
  plainText,
  type CaptionBox,
  type CaptionWord,
  type TimedWord,
} from "./captions/words";

/** Where a clip's media comes from: a library file, or a delivered output of a job. */
/** A file a clip plays or shows: a library file or a job's delivered output. */
export type FileSource =
  | { kind: "asset"; assetId: string }
  | { kind: "output"; jobId: string; index: number };

/** A clip's source: a file, or, for a picture, one of the team's actors (live's Avatar, #244). */
export type ClipSource = FileSource | { kind: "actor"; actorId: string };

export type TrackKind = "video" | "audio" | "text" | "effect" | "caption";

export interface Track {
  id: string;
  kind: TrackKind;
  /** Silences every clip on it. */
  muted: boolean;
  /**
   * The main track magnet (spec magnet-ripple, D6): its clips lie back to back from 0, so every
   * edit on it closes up or pushes on. At most one track, a video track, has it.
   */
  magnet: boolean;
}

interface Placed {
  id: string;
  trackId: string;
  /** Where on the video the clip shows, and until when. */
  startMs: number;
  endMs: number;
  /** Live's Lock: no edit changes the clip until it is unlocked. */
  locked: boolean;
  /** Live's Hide (#267): a picture or a text not drawn; a video's sound still plays. */
  hidden: boolean;
}

/** A video read from `inMs` of its own time, with its own sound unless `muted`. */
export interface VideoClip extends Placed, Look {
  kind: "video";
  source: FileSource;
  inMs: number;
  /**
   * How fast it reads its file, `MIN_SPEED` to `MAX_SPEED` (spec clip-speed): 2 reads twice its length.
   * With a curve, its mean, unrounded: its file span over its length (spec clip-speed-curves).
   */
  speed: number;
  /** Its speed curve, in its file's time (spec clip-speed-curves); none plays its one speed. */
  speedPoints: readonly SpeedPoint[];
  /** Which curve it plays, the Curve tab's pressed tile: set exactly when `speedPoints` are. */
  speedCurve: SpeedCurveName | null;
  muted: boolean;
  /** 0 to 2: live's Volume, and up to 200 % (spec volume-boost). */
  volume: number;
  /** Its volume keyframes, in its file's time (spec volume-keyframes); none is flat at `volume`. */
  volumePoints: readonly VolumePoint[];
  fadeInMs: number;
  fadeOutMs: number;
  /** Live's Animations (#266), in the order they were added. */
  animations: readonly Animation[];
  /**
   * The file it played before its background was removed (spec remove-background.md), which
   * Restore original puts back; null for a video as it was added.
   */
  originalSource: FileSource | null;
}

/** A still, shown for the clip's length. */
export interface ImageClip extends Placed, Look {
  kind: "image";
  source: ClipSource;
  animations: readonly Animation[];
}

/** A sound read from `inMs` of its own time, heard unless `muted` (live's Mute). */
export interface AudioClip extends Placed {
  kind: "audio";
  source: FileSource;
  inMs: number;
  /** How fast it reads its file, its pitch kept (spec clip-speed); with a curve, its mean. */
  speed: number;
  /** Its speed curve and its name, as a video's (spec clip-speed-curves). */
  speedPoints: readonly SpeedPoint[];
  speedCurve: SpeedCurveName | null;
  muted: boolean;
  volume: number;
  /** Its volume keyframes, in its file's time (spec volume-keyframes). */
  volumePoints: readonly VolumePoint[];
  fadeInMs: number;
  fadeOutMs: number;
  /**
   * Its beats (spec beats.md): times in its own file, rising, kept whatever span it reads; one
   * outside the span does not show (`beatTimes`), and a trim back shows it again.
   */
  beatsMs: readonly number[];
}

/**
 * A video's or a sound's level at a time of its own file (spec volume-keyframes): the straight line
 * through its points is its level, held flat before the first and after the last.
 */
export interface VolumePoint {
  atMs: number;
  volume: number;
}

/** A text over the video, as live's Parameters set it (#241 part 2). */
export interface TextClip extends Placed {
  kind: "text";
  text: string;
  style: TextStyle;
  /** Live's Animations (#266): every In/Out preset, no combo. */
  animations: readonly Animation[];
}

/**
 * Live's effect (#265): `effect` over every picture and text of the tracks below its own for its
 * time, its animation running 0 to 1 over the clip. Hidden, it is not applied.
 */
export interface EffectClip extends Placed {
  kind: "effect";
  effect: EffectKind;
  /** Live's Configuration of a Pixi filter (#265 part 2): the values changed, by name. */
  values: Readonly<Record<string, EffectValue>>;
}

/**
 * Live's caption (#239): its words timed from its start (each word in its own colour as it is
 * spoken), drawn centred on `box` in the project's caption style. It sits only on the caption
 * track; it may hold no word (live keeps a caption whose text was cleared).
 */
export interface CaptionClip extends Placed {
  kind: "caption";
  words: readonly CaptionWord[];
  box: CaptionBox;
  /** Its own uniform scale on the canvas, times the style's size, about its drawn lines' centre (#560). */
  scale: number;
  /** Its own turn on the canvas, degrees clockwise about its drawn lines' centre (#560). */
  rotation: number;
}

export type Clip = VideoClip | ImageClip | AudioClip | TextClip | EffectClip | CaptionClip;
export type MediaClip = VideoClip | ImageClip | AudioClip;
export type MediaKind = MediaClip["kind"];

/**
 * Live's transition (#240): `kind` over the cut where the picture `fromId` ends and the next
 * picture of its track, `toId`, starts, lasting `durationMs` centred on the cut. Inside it, the
 * first picture holds its last frame and the second its first where the other's time lies.
 */
export interface Transition {
  id: string;
  fromId: string;
  toId: string;
  kind: TransitionKind;
  durationMs: number;
}

/** A note pinned to a moment of the video (spec markers): live's MCP marker, saved with the project. */
export interface Marker {
  id: string;
  atMs: number;
  label: string;
  /** `#RRGGBB`, one of `MARKER_COLORS` from the editor. */
  color: string;
}

export interface Timeline {
  /** Top to bottom; the top track draws in front. */
  tracks: readonly Track[];
  clips: readonly Clip[];
  transitions: readonly Transition[];
  /** Live's captions' look (#239), the same on every caption. */
  captions: CaptionSettings;
  /** The frame's shape (decision 21): live's ratio menu changes it as one undo step. Saved as the
   * project's `aspect`, beside the wire's timeline. */
  aspect: EditAspect;
  /** Notes pinned to moments (spec markers), by time; every other edit leaves them where they are. */
  markers: readonly Marker[];
}

/** The shortest a clip may be. */
export const MIN_SPAN_MS = 100;
/** How long a still, a title or an effect lasts when added (live's 5 s). */
export const STILL_MS = 5000;
/** The contract's limits: clips per kind, tracks, and the video's length. */
export const MAX_VISUALS = 50;
export const MAX_SOUNDS = 20;
export const MAX_TITLES = 50;
export const MAX_EFFECTS = 20;
export const MAX_TRACKS = 20;
export const MAX_TOTAL_MS = 300_000;
/** The contract's furthest time in a clip's own file (`in_s`, spec clip-speed): a day, past any file a team edits. */
export const MAX_FILE_MS = 86_400_000;
/** Live's Fade sliders: 0 to 5000 ms. */
export const MAX_FADE_MS = 5000;
/** A clip's level (spec volume-boost): live's 0 to 100 %, and up to 200 % for quiet footage. */
export const MAX_VOLUME = 2;
/** A clip's volume keyframes (spec volume-keyframes): at most the contract's 100. */
export const MAX_VOLUME_POINTS = 100;
/** One frame of the render and the preview (spec E2): 30 a second. A keyframe this near the playhead is on it. */
export const FRAME_MS = 1000 / 30;
/** Markers a project holds (spec markers): live's MCP adds at most 100 at a time. */
export const MAX_MARKERS = 100;
/** A new marker's label, as live's MCP names it. */
export const MARKER_LABEL = "Marker";
/** The markers' colours, live's MCP's named ones, its default blue first (spec markers). */
export const MARKER_COLORS = [
  "#0000FF", // raw-color: a marker's colour is data, live's MCP's "blue"
  "#FF0000", // raw-color: live's "red"
  "#008000", // raw-color: "green"
  "#FFA500", // raw-color: "orange"
  "#800080", // raw-color: "purple"
  "#FFD700", // raw-color: "gold"
] as const;
/** A video's or a sound's speed (spec clip-speed), kept with its curve's math. */
export { MAX_SPEED, MIN_SPEED };
/** A sound's beats (spec beats.md): at most 2,000, each a time in its file within `MAX_FILE_MS`. */
export const MAX_BEATS = 2000;
/** M on a beat removes it with the playhead within 100 ms of it: live's 10 px snap at 100% zoom. */
export const BEAT_REACH_MS = 100;
/** Live's cut: the second picture starts at most 0.1 s after the first ends. */
export const JUNCTION_GAP_MS = 100;
/** Live's transitions: added at 2 s, never shorter than 0.1 s. */
export const TRANSITION_MS = 2000;
export const MIN_TRANSITION_MS = 100;

/** The track each media kind lives on. */
export const TRACK_OF: Record<Clip["kind"], TrackKind> = {
  video: "video",
  image: "video",
  audio: "audio",
  text: "text",
  effect: "effect",
  caption: "caption",
};

/** An empty timeline: one video track, without the magnet. */
export const EMPTY_TIMELINE: Timeline = {
  tracks: [{ id: "video-main", kind: "video", muted: false, magnet: false }],
  clips: [],
  transitions: [],
  captions: DEFAULT_CAPTIONS,
  aspect: "16:9",
  markers: [],
};

/** A new project's, as the server makes one: its main video track with the magnet on (D6). */
export const NEW_TIMELINE: Timeline = {
  ...EMPTY_TIMELINE,
  tracks: [{ id: "video-main", kind: "video", muted: false, magnet: true }],
};

export type TimelineEdit =
  /** The saved timeline, after a load, a save or a reload on a conflict. */
  | { type: "load"; timeline: Timeline }
  /**
   * The assistant's Revert (#242): the timeline as it was before a plan, as one more undo step
   * (unlike a load, which starts the history again).
   */
  | { type: "restore"; timeline: Timeline }
  /**
   * Another save of the project merged in (spec live-sync-merge.md): `theirs` against `base`, the
   * timeline this tab last saved or took in. Where this timeline holds what `base` held, it takes
   * theirs; this tab's unsaved edits it keeps. `present`, the draft's present, when the history
   * merges an older or a newer step: only the present's own unsaved values win there. Refused
   * when the merge breaks a bound.
   */
  | { type: "sync"; base: Timeline; theirs: Timeline; present?: Timeline }
  /** Live's ratio menu (decision 21): the frame's shape; every item keeps its pixels. */
  | { type: "setAspect"; aspect: EditAspect }
  /**
   * Adds a library or job file at the end of the first track of its kind (made below the others
   * if there is none): a video or a sound whole (`sourceMs`), a still for `STILL_MS`. A video or a
   * sound without a usable length is not added, nor a clip past its kind's limit. With `startMs`
   * (the assistant's, live's `clip.add`; G11 PR 2a) it starts there instead, on the first track of
   * its kind free over its whole span, else on a new track of that kind above every other.
   */
  | {
      type: "addClip";
      id: string;
      kind: MediaKind;
      source: ClipSource;
      sourceMs: number | null;
      newTrackId: string;
      startMs?: number;
    }
  /**
   * Live's effect tile, clicked or dropped (#265): `effect` from 0 to 5 s on a new effect track
   * above every other. Nothing is added past the effects' count or the tracks'.
   */
  | { type: "addEffect"; id: string; effect: EffectKind; newTrackId: string }
  /** A title at `startMs` on the first text track (made on top if there is none). */
  | {
      type: "addTitle";
      id: string;
      text: string;
      startMs: number;
      endMs: number;
      style: TextStyle;
      newTrackId: string;
    }
  /**
   * Moves a clip to start at `startMs`, on `trackId` if it names a track of the same kind. A move
   * that would overlap another clip there is not made.
   */
  | { type: "moveClip"; id: string; startMs: number; trackId?: string }
  /**
   * Live's drop on a guide between tracks (issue 828 bug 28): the clip on a new track of its kind
   * (`newTrackId`) at `index` in the stack (0 above every other, the tracks' count below them),
   * from `startMs`. A clip alone on its track let go right above or below it keeps its track. No
   * track is made past the tracks' count, nor a second caption track.
   */
  | { type: "moveToNewTrack"; id: string; startMs: number; index: number; newTrackId: string }
  /**
   * Daniil's reorder (issue 828 bug 28 item 3, richer than live): the clip let go on `ontoId`
   * takes its place. On the same track, the clips from there up to the clip's old place close up
   * toward it by the clip's length, so of two clips either one let go on the other swaps them; from
   * another track, the clip goes in at `ontoId`'s start and it and every later clip of the track
   * shift later. Nothing moves while a clip that would shift is locked, or past the longest video.
   */
  | { type: "reorderClip"; id: string; ontoId: string }
  /**
   * Trims a clip's start or end on the timeline: a video or a sound reads its source from a later
   * or earlier point with its start; neither passes its source (the end's `sourceMs`, `null`
   * while unknown), a neighbour, or the shortest span. A drag's trims carry the timeline as the
   * drag found it (`origin`) and count from it, so a handle moved back gives back what it took (spec
   * magnet-ripple decision 8).
   */
  | { type: "trimStart"; id: string; startMs: number; origin?: Timeline }
  | { type: "trimEnd"; id: string; endMs: number; sourceMs: number | null; origin?: Timeline }
  /** Sets a video's or a sound's in and out points of its source; its start stays. */
  | { type: "setRange"; id: string; inMs: number; outMs: number; sourceMs: number | null }
  /** Sets a clip's own fields (mute, volume, fades, text, position). */
  | { type: "setClip"; id: string; patch: ClipPatch }
  /** Removes a clip; with `ripple` (⇧Delete), every later clip of its track moves up by its length. */
  | { type: "removeClip"; id: string; ripple?: boolean }
  /** Cuts a clip in two at `atMs` (live's Split): the second half is `newId`. */
  | { type: "split"; id: string; atMs: number; newId: string }
  /** A copy of a clip at the same time on a new track right above its own (live's Duplicate). */
  | { type: "duplicate"; id: string; newId: string; newTrackId: string }
  /**
   * Live's Separate Audio: a video's sound as a clip of its own (`newId`), read from the same
   * file over the same span with the video's level and fades, on the first audio track free
   * there (or a new one below the rest); the video is muted.
   */
  | { type: "separateAudio"; id: string; newId: string; newTrackId: string }
  /** Live's Lock and Unlock: a locked clip takes no other edit. */
  | { type: "setLock"; id: string; locked: boolean }
  /**
   * Live's import (#239): `captions` replace every caption, on the caption track (made on top if
   * there is none, as `newTrackId`). Locked ones go too, as live's.
   */
  | { type: "replaceCaptions"; captions: readonly NewCaption[]; newTrackId: string }
  /** Live's "Delete captions": every caption goes; the caption track stays. */
  | { type: "removeCaptions" }
  /**
   * Live's caption look (#264): a preset's click or a theme editor's change, on every caption; with
   * `recut`, every caption replaced by those (a preset's and a Paragraph change's re-cut).
   */
  | {
      type: "setCaptions";
      captions: CaptionSettings;
      /**
       * A re-cut (a preset's or a Paragraph change's): every caption cut again from the
       * captions' own words as they are now, by the face's measures taken beforehand, the new
       * ones `idPrefix-0`, `idPrefix-1`... Refused, the timeline unchanged, while a caption is
       * locked or when the cut would pass the contract's counts.
       */
      cut: CaptionCut | null;
      idPrefix: string;
      newTrackId: string;
      /** Each unlocked caption's new box (Position and the box's numbers, #264 part 2). */
      boxes?: (caption: CaptionClip) => CaptionBox;
    }
  /** A caption's words as the Transcript typed them (`realign`). */
  | { type: "setCaptionWords"; id: string; words: readonly CaptionWord[] }
  /**
   * Live's Enter in the Transcript: the caption ends `atMs` after it starts with `first`, and
   * `newId` starts there with `second` (each timed from its own start), in the same box.
   */
  | {
      type: "splitCaption";
      id: string;
      newId: string;
      atMs: number;
      first: readonly CaptionWord[];
      second: readonly CaptionWord[];
    }
  /**
   * Several edits as one step (an edit of every chosen clip), each as it would be alone. Refused
   * only when every one of them is, for the first one's reason.
   */
  | {
      type: "many";
      edits: readonly TimelineEdit[];
      /** An undo step of its own, never joined to a run: an assistant's plan step (#242). */
      alone?: boolean;
    }
  /**
   * Live's tile and "+": `kind` on the cut from `fromId` to `toId`. A cut with a transition only
   * changes its kind; one without gets a new one (`id`), live's 2 s or as long as its shorter
   * picture. Nothing is made off a cut.
   */
  | { type: "setTransition"; id: string; fromId: string; toId: string; kind: TransitionKind }
  /** Live's Duration slider: from 0.1 s to its shorter picture's length. */
  | { type: "setTransitionDuration"; id: string; durationMs: number }
  /** Live's "None" card, or Delete on a chosen transition. */
  | { type: "removeTransition"; id: string }
  /**
   * Live's Paste: copies of clips, the earliest at `atMs` and the others at the same distance
   * after it, each on its own track if that is free there, else on the first track of its kind
   * free there, else on a new one (`newTrackIds`, one per clip). Nothing is pasted if one would
   * not fit, past the video's longest or its kind's count.
   */
  | {
      type: "paste";
      clips: readonly Clip[];
      atMs: number;
      newIds: readonly string[];
      newTrackIds: readonly string[];
    }
  /**
   * A rough cut (#1001, spec assistant-rough-cut): each span of the video's time taken out of
   * every track and the rest closed up, one step. A clip across a span keeps its parts outside it,
   * a video or a sound reading on from the span's end and a caption keeping its words in time; a
   * part after the first is `idPrefix-n`. Refused with no span, for a locked clip it would cut or
   * move, or past the contract's counts.
   */
  | { type: "cutSpans"; spans: readonly Span[]; idPrefix: string }
  /**
   * The main track magnet (spec magnet-ripple, D6): on, the bottom video track's (a new one at
   * the bottom, `newTrackId`, when there is none), its clips then packed; off, cleared.
   */
  | { type: "setMagnet"; on: boolean; newTrackId: string }
  /**
   * CapCut's Q and W: the part of a clip before the playhead (`start`) or after it (`end`) taken
   * off, as a trim to it; on the magnetic track the later clips close up.
   */
  | { type: "trimToPlayhead"; id: string; atMs: number; side: "start" | "end" }
  /**
   * A video's or a sound's speed (spec clip-speed), held to 0.25× to 4× in hundredths: it keeps its
   * start and the span of its file it reads, so it ends at that span over the speed. With
   * `ripple`, the later clips of its track move by the change; without it, the next one it would
   * run into refuses it. A slider's run carries the timeline it began on (`origin`), as a trim's.
   */
  | { type: "setSpeed"; id: string; speed: number; ripple: boolean; origin?: Timeline }
  /**
   * A video's or a sound's speed curve (spec clip-speed-curves), its points in its file's time held
   * to the contract's, or none (`null`): back to one speed at 1×. It keeps its start and the span of
   * its file it reads, so it lasts that span along the curve. `ripple` and `origin` as `setSpeed`'s;
   * `setSpeed` takes a curve away.
   */
  | {
      type: "setSpeedCurve";
      id: string;
      curve: { name: SpeedCurveName; points: readonly SpeedPoint[] } | null;
      ripple: boolean;
      origin?: Timeline;
    }
  /** A marker at a time (spec markers), "Marker" in blue unless named; refused past 100. */
  | { type: "addMarker"; id: string; atMs: number; label?: string; color?: string }
  /** A marker moved, renamed or recoloured; an empty label keeps the last. A drag's run is one step. */
  | { type: "setMarker"; id: string; patch: { atMs?: number; label?: string; color?: string } }
  | { type: "removeMarker"; id: string }
  /**
   * M with a sound chosen (spec beats.md): the beat at the playhead `atMs`, in the sound's own
   * time. The nearest beat that shows within `BEAT_REACH_MS` goes; else one is added there.
   */
  | { type: "toggleBeat"; id: string; atMs: number }
  /**
   * Remove background (spec remove-background.md): the video plays `source`, the job's output of
   * its file (the same length and times), keyed by Bria's green; its first file is kept as its
   * original. Its times, in point, look and sound stay.
   */
  | { type: "removeBackground"; id: string; source: FileSource }
  /** Restore original: the video plays its original file again, its key cleared. */
  | { type: "restoreOriginal"; id: string }
  /**
   * Find beats and Clear beats (spec find-beats.md): the sound's beats, in its file's time, all
   * replaced; held as the contract keeps them (whole, within `MAX_FILE_MS`, rising, at most 2,000).
   */
  | { type: "setBeats"; id: string; beatsMs: readonly number[] }
  /**
   * The Volume slider at the playhead (spec volume-keyframes): with no keyframes the clip's flat
   * volume; on one (within a frame) that keyframe; between them a new one at the playhead.
   */
  | { type: "setVolumeAt"; id: string; atMs: number; volume: number }
  /** The diamond: a keyframe at the playhead, at the level there, or the one there taken off. */
  | { type: "addVolumePoint"; id: string; atMs: number }
  | { type: "removeVolumePoint"; id: string; atMs: number };

/** A span of the video's time, from `startMs` to `endMs`. */
export interface Span {
  startMs: number;
  endMs: number;
}

/** A caption as an import cuts it: its place on the video, its words and its box. */
export interface NewCaption {
  id: string;
  startMs: number;
  endMs: number;
  words: readonly CaptionWord[];
  box: CaptionBox;
  /** Live's Hide (a re-cut keeps it where the old caption had it). */
  hidden?: boolean;
}

/** How a re-cut measures: the caption face's metrics and the frame, in live's pixels. */
export interface CaptionCut {
  metrics: CaptionMetrics;
  frame: { width: number; height: number };
}

export interface ClipPatch extends Partial<Look> {
  muted?: boolean;
  volume?: number;
  fadeInMs?: number;
  fadeOutMs?: number;
  text?: string;
  /** Live's Hide: a picture, a text or an effect (a sound has nothing to hide). */
  hidden?: boolean;
  /** A text's style: any of its values (text-style.ts holds them to their ranges). */
  style?: TextStylePatch;
  /** An effect's Configuration: values to set, by name (the panel holds them to their ranges). */
  effectValues?: Readonly<Record<string, EffectValue>>;
  /** A picture's or a text's animations, the whole list (#266; the popover holds each). */
  animations?: readonly Animation[];
  /** A caption's own uniform scale (#560): `rotation` (from the look) turns it. */
  scale?: number;
}

/** A caption's own scale on the canvas (#560), the contract's range. */
const MIN_CAPTION_SCALE = 0.1;
const MAX_CAPTION_SCALE = 10;

function whole(ms: number): number {
  return Number.isFinite(ms) ? Math.round(ms) : 0;
}

/** A marker's time, whole and within the longest video. */
function markerTime(atMs: number): number {
  return Math.min(Math.max(whole(atMs), 0), MAX_TOTAL_MS);
}

/** A marker's label as the contract keeps it: one line of its words, at most 100 characters; null for none. */
function labelOf(label: string | undefined): string | null {
  const kept = Array.from(plainText(label ?? "")).slice(0, 100).join("").trimEnd();
  return kept === "" ? null : kept;
}

/** Markers by time, the earlier first; one at the same time keeps its place. */
function byTime(markers: readonly Marker[]): readonly Marker[] {
  return [...markers].sort((one, other) => one.atMs - other.atMs);
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

type Group = "visual" | "sound" | "title" | "effect" | "caption";

const KIND_LIMIT: Record<Group, number> = {
  visual: MAX_VISUALS,
  sound: MAX_SOUNDS,
  title: MAX_TITLES,
  effect: MAX_EFFECTS,
  caption: MAX_CAPTIONS,
};

function group(kind: Clip["kind"]): Group {
  if (kind === "audio") return "sound";
  if (kind === "effect" || kind === "caption") return kind;
  return kind === "text" ? "title" : "visual";
}

/** A track the reducer makes: heard, and not the main track. */
function newTrack(id: string, kind: TrackKind): Track {
  return { id, kind, muted: false, magnet: false };
}

/** Whether a track is the magnetic one (spec magnet-ripple): its clips are packed after every edit. */
export function magnetic(timeline: Timeline, trackId: string): boolean {
  return timeline.tracks.some((track) => track.id === trackId && track.magnet);
}

/** Text, effect and caption tracks are made on top, the others below the rest. */
function placeTrack(tracks: readonly Track[], track: Track): readonly Track[] {
  return track.kind === "video" || track.kind === "audio" ? [...tracks, track] : [track, ...tracks];
}

function full(timeline: Timeline, kind: Clip["kind"]): boolean {
  const g = group(kind);
  return timeline.clips.filter((clip) => group(clip.kind) === g).length >= KIND_LIMIT[g];
}

/** The other clips of a track, earliest first. */
export function clipsOn(timeline: Timeline, trackId: string, except?: string): Clip[] {
  return timeline.clips
    .filter((clip) => clip.trackId === trackId && clip.id !== except)
    .sort((a, b) => a.startMs - b.startMs);
}

/** Whether a span would lie over another clip of a track than `id`. */
export function overlaps(timeline: Timeline, trackId: string, id: string, start: number, end: number) {
  return overlapped(timeline, trackId, id, start, end) !== undefined;
}

/** The first other clip of a track than `id` that a span would lie over. */
function overlapped(timeline: Timeline, trackId: string, id: string, start: number, end: number): Clip | undefined {
  return clipsOn(timeline, trackId, id).find((clip) => start < clip.endMs && end > clip.startMs);
}

/**
 * The free room around a clip on its track: the previous clip's end and the next one's start. On
 * the magnetic track its neighbours move with it, so only the video's longest bounds it.
 */
function room(timeline: Timeline, clip: Clip): { low: number; high: number } {
  if (magnetic(timeline, clip.trackId)) {
    const spare = Math.max(0, MAX_TOTAL_MS - trackEnd(timeline, clip.trackId));
    return { low: clip.startMs - spare, high: clip.endMs + spare };
  }
  const others = clipsOn(timeline, clip.trackId, clip.id);
  const low = Math.max(0, ...others.filter((o) => o.endMs <= clip.startMs).map((o) => o.endMs));
  const after = others.filter((o) => o.startMs >= clip.endMs).map((o) => o.startMs);
  return { low, high: Math.min(MAX_TOTAL_MS, ...after) };
}

/**
 * Where a video or a sound reads its file at `atMs` of the timeline: its in-point moved at its
 * speed (spec clip-speed) or along its curve (`fileMsAt`, spec clip-speed-curves), held to the
 * contract's furthest time in a file.
 */
function readsAt(clip: VideoClip | AudioClip, atMs: number): number {
  return Math.min(MAX_FILE_MS, Math.round(fileMsAt(clip, atMs)));
}

/**
 * The part of a video or a sound shown from `fromMs` to `toMs` of the timeline (a trim, a split, a
 * cut): where it reads its file from, and its speed. One speed is kept; a curve's part plays its
 * own mean, its span over its length, so its span stays the curve's (spec clip-speed-curves).
 */
function partOf(clip: VideoClip | AudioClip, fromMs: number, toMs: number): { inMs: number; speed: number } {
  const inMs = readsAt(clip, fromMs);
  if (!curved(clip)) return { inMs, speed: clip.speed };
  return { inMs, speed: clamp((readsAt(clip, toMs) - inMs) / (toMs - fromMs), MIN_SPEED, MAX_SPEED) };
}

/**
 * A curve laid over the file span a clip reads from `inMs` (spec clip-speed-curves): the length it
 * gives the clip, in whole milliseconds, at least the shortest span and within 0.25× to 4× of the
 * span, and its mean speed, unrounded, so the length reads the span exactly (decision 4).
 */
function laidOver(points: readonly SpeedPoint[], inMs: number, spanMs: number): { lengthMs: number; speed: number } {
  const factor = lengthFactor(curveOver(points, inMs, spanMs));
  const lengthMs = clamp(
    Math.round(spanMs * factor),
    Math.max(MIN_SPAN_MS, Math.ceil(spanMs / MAX_SPEED)),
    Math.max(MIN_SPAN_MS, Math.floor(spanMs / MIN_SPEED)),
  );
  return { lengthMs, speed: clamp(spanMs / lengthMs, MIN_SPEED, MAX_SPEED) };
}

/**
 * A video or a sound at a new speed or curve (`setSpeed`, `setSpeedCurve`), its start kept and its
 * end at `changes.endMs`: with `ripple` the later clips of its track move by the change, and the
 * magnetic track packs them either way; without, the next one it would run into refuses it.
 */
function resped(
  timeline: Timeline,
  clip: VideoClip | AudioClip,
  changes: Pick<VideoClip, "speed" | "endMs"> & Partial<Pick<VideoClip, "speedPoints" | "speedCurve">>,
  ripple: boolean,
): Timeline | string {
  const { endMs } = changes;
  if (endMs > MAX_TOTAL_MS) return REFUSALS.longest();
  const change = endMs - clip.endMs;
  const later = clipsOn(timeline, clip.trackId, clip.id).filter((each) => each.startMs >= clip.endMs);
  // The magnetic track closes up or pushes on by its packing; elsewhere `ripple` moves the rest.
  const main = magnetic(timeline, clip.trackId);
  const rippled = ripple && !main;
  if (rippled) {
    const locked = later.find((each) => each.locked && change !== 0);
    if (locked !== undefined) return REFUSALS.locked(locked.id);
    if (later.some((each) => each.endMs + change > MAX_TOTAL_MS)) return REFUSALS.longest();
  } else if (!main && later[0] !== undefined && endMs > later[0].startMs) {
    return REFUSALS.overlap(clip.id, later[0].id);
  }
  const moved = new Set(rippled ? later.map((each) => each.id) : []);
  const clips = timeline.clips.map((each) => {
    if (each.id === clip.id) return { ...clip, ...changes };
    return moved.has(each.id) ? { ...each, startMs: each.startMs + change, endMs: each.endMs + change } : each;
  });
  return { ...timeline, clips };
}

/** The span of its file a video or a sound reads, in whole milliseconds, which a curve keeps. */
export function spanOf(clip: VideoClip | AudioClip): number {
  return Math.round(clipMs(clip) * clip.speed);
}

/**
 * How long a video or a sound lasts with `curve` laid over the span it reads, as `setSpeedCurve`
 * makes it, or at 1× with none (spec clip-speed-curves-tab: each tile shows it).
 */
export function lengthWith(clip: VideoClip | AudioClip, curve: readonly CurvePoint[] | null): number {
  const spanMs = spanOf(clip);
  if (curve === null) return Math.max(MIN_SPAN_MS, spanMs);
  const points = heldSpeedPoints(pointsOver(curve, clip.inMs, spanMs), MAX_FILE_MS, clip.inMs, clip.inMs + spanMs);
  return laidOver(points, clip.inMs, spanMs).lengthMs;
}

/** Where a volume keyframe lies on the timeline: its file time at the clip's speed or along its curve. */
export function volumeMsOf(clip: Leveled, point: VolumePoint): number {
  return clipMsAt(clip, point.atMs);
}

/**
 * The volume keyframe nearest a time of the timeline, within a frame of it, if any (the diamond's
 * "on one"): a faster speed can bring two within a frame, and the one the playhead is on is meant.
 */
export function volumePointNear(clip: VideoClip | AudioClip, atMs: number): VolumePoint | undefined {
  const off = (point: VolumePoint) => Math.abs(volumeMsOf(clip, point) - atMs);
  return clip.volumePoints
    .filter((point) => off(point) <= FRAME_MS)
    .reduce<VolumePoint | undefined>((near, point) => (near === undefined || off(point) < off(near) ? point : near), undefined);
}

/**
 * Where Previous and Next keyframe go: the times of the keyframes the clip shows, before and after a
 * time of the timeline. On a keyframe, the ones beside it, however near a speed put them; else the
 * nearest on each side.
 */
export function volumeNeighbours(clip: VideoClip | AudioClip, atMs: number): { before: number | undefined; after: number | undefined } {
  const shown = clip.volumePoints
    .map((point) => ({ point, ms: volumeMsOf(clip, point) }))
    .filter(({ ms }) => ms >= clip.startMs && ms <= clip.endMs);
  const near = volumePointNear(clip, atMs);
  const on = shown.findIndex(({ point }) => point === near);
  if (on >= 0) return { before: shown[on - 1]?.ms, after: shown[on + 1]?.ms };
  return { before: shown.filter(({ ms }) => ms < atMs).at(-1)?.ms, after: shown.find(({ ms }) => ms > atMs)?.ms };
}

/** What a level is read from: a video or a sound, or the engine's sound of one. */
export type Leveled = Pick<AudioClip, "startMs" | "endMs" | "inMs" | "speed" | "speedPoints" | "volume" | "volumePoints">;

/**
 * A video's or a sound's level at a time of the timeline (spec volume-keyframes): its volume, or
 * the straight line through its keyframes in its file's time, flat before the first and after the last.
 */
export function volumeAt(clip: Leveled, atMs: number): number {
  const points = clip.volumePoints;
  const [first] = points;
  if (first === undefined) return clip.volume;
  const fileMs = fileMsAt(clip, atMs);
  if (fileMs <= first.atMs) return first.volume;
  const after = points.findIndex((point) => point.atMs > fileMs);
  if (after < 0) return points.at(-1)!.volume;
  const from = points[after - 1]!;
  const to = points[after]!;
  return from.volume + ((to.volume - from.volume) * (fileMs - from.atMs)) / (to.atMs - from.atMs);
}

/** The clip by id while it may be changed, else why not: it is not there, or it is locked. */
function changeable(timeline: Timeline, id: string): Clip | string {
  const clip = timeline.clips.find((each) => each.id === id);
  if (clip === undefined) return REFUSALS.noClip(id);
  return clip.locked ? REFUSALS.locked(id) : clip;
}

/** Changes one clip, unless it is gone or locked, or the change refuses with its reason. */
function mapClip(timeline: Timeline, id: string, change: (clip: Clip) => Clip | string): Timeline | string {
  const clip = changeable(timeline, id);
  if (typeof clip === "string") return clip;
  const changed = change(clip);
  if (typeof changed === "string") return changed;
  return { ...timeline, clips: timeline.clips.map((each) => (each.id === id ? changed : each)) };
}

/**
 * Drops the tracks left empty, but always keeps a video track (the magnetic one, else the top
 * one, if all are empty), the magnetic track and the caption track (live's stays when its
 * captions go).
 */
function tidy(timeline: Timeline): Timeline {
  const used = new Set(timeline.clips.map((clip) => clip.trackId));
  const main = timeline.tracks.some((track) => track.kind === "video" && used.has(track.id))
    ? undefined
    : (timeline.tracks.find((track) => track.magnet) ?? timeline.tracks.find((track) => track.kind === "video"));
  const tracks = timeline.tracks.filter(
    (track) => used.has(track.id) || track === main || track.magnet || track.kind === "caption",
  );
  return tracks.length === timeline.tracks.length ? timeline : { ...timeline, tracks };
}

/** The first track of a kind, or a new one: text tracks on top, others below the rest. */
/**
 * The first track of `kind` free from `start` to `end`, or a new one above every other (live's
 * `manageTracks`); "" when there is no room for another track.
 */
function freeTrackAt(
  timeline: Timeline,
  kind: TrackKind,
  id: string,
  start: number,
  end: number,
  newTrackId: string,
): [Timeline, string] {
  const free = timeline.tracks.find((track) => track.kind === kind && !overlaps(timeline, track.id, id, start, end));
  if (free !== undefined) return [timeline, free.id];
  if (timeline.tracks.length >= MAX_TRACKS) return [timeline, ""];
  const track = newTrack(newTrackId, kind);
  return [{ ...timeline, tracks: [track, ...timeline.tracks] }, newTrackId];
}

function trackFor(timeline: Timeline, kind: TrackKind, newTrackId: string): [Timeline, string] {
  const found = timeline.tracks.find((track) => track.kind === kind);
  if (found !== undefined) return [timeline, found.id];
  if (timeline.tracks.length >= MAX_TRACKS) return [timeline, ""];
  const track = newTrack(newTrackId, kind);
  return [{ ...timeline, tracks: placeTrack(timeline.tracks, track) }, newTrackId];
}

function trackEnd(timeline: Timeline, trackId: string): number {
  return Math.max(0, ...clipsOn(timeline, trackId).map((clip) => clip.endMs));
}

function fits(start: number, end: number): boolean {
  return end - start >= MIN_SPAN_MS && end <= MAX_TOTAL_MS;
}

/** What each kind of track holds, in a reason's words. */
const HOLDS: Record<TrackKind, string> = {
  video: "videos and pictures",
  audio: "sounds",
  text: "texts",
  effect: "effects",
  caption: "captions",
};

/**
 * Every reason the reducer refuses an edit for (#601), by name: `refusal` gives one of these, and
 * `editTimeline` then gives back the timeline as it was. A clip is named by its id, as the
 * assistant reads it.
 */
export const REFUSALS = {
  noClip: (id: string) => `There is no clip ${id} on the timeline.`,
  locked: (id: string) => `Clip ${id} is locked: unlock it first.`,
  full: (kind: Clip["kind"]) => `The project holds the most ${HOLDS[TRACK_OF[kind]]} it can (${KIND_LIMIT[group(kind)]}).`,
  tracks: () => `The project holds the most tracks it can (${MAX_TRACKS}).`,
  longest: () => `That would end past ${MAX_TOTAL_MS / 1000} s, the longest a video can be.`,
  noLength: () => "The file has no length the editor can use.",
  actor: () => "An actor goes on the timeline only as a picture.",
  sameAspect: () => "The project already has that shape.",
  noTrack: (trackId: string) => `There is no track ${trackId}.`,
  trackKind: (id: string, trackId: string, kind: TrackKind) => `Track ${trackId} holds ${HOLDS[kind]}: clip ${id} cannot go there.`,
  overlap: (id: string, otherId: string) => `Clip ${id} would lie over clip ${otherId} there.`,
  trackTaken: (trackId: string) => `There is a track ${trackId} already.`,
  captionStays: () => "A caption stays on the caption track, at its time.",
  captionCopy: () => "A caption is not copied: a project has one caption track.",
  ontoItself: (id: string) => `Clip ${id} cannot take its own place.`,
  tooShort: (id: string) => `Clip ${id} is shorter than 0.1 s: there is nothing to trim.`,
  noFile: (id: string) => `Clip ${id} is not a video or a sound: it reads no file.`,
  outside: (id: string, atMs: number) => `The split at ${atMs} ms is not inside clip ${id}.`,
  nearEnd: (id: string, atMs: number) => `The split at ${atMs} ms leaves a part of clip ${id} shorter than 0.1 s.`,
  notVideo: (id: string) => `Clip ${id} is not a video: only a video has a sound of its own.`,
  alreadyLocked: (id: string) => `Clip ${id} is already locked.`,
  notLocked: (id: string) => `Clip ${id} is not locked.`,
  nothingToPaste: () => "There is nothing to paste.",
  pasteIds: () => "Every pasted clip needs a new id and a new track id.",
  notCaption: (id: string) => `Clip ${id} is not a caption.`,
  captionCount: () => `A project holds at most ${MAX_CAPTIONS} captions and ${MAX_TIMELINE_CAPTION_WORDS} caption words.`,
  noCut: (fromId: string, toId: string) => `Clips ${fromId} and ${toId} do not meet at a cut.`,
  sameTransition: (fromId: string, toId: string) => `The cut from ${fromId} to ${toId} already has that transition.`,
  noTransition: (id: string) => `There is no transition ${id}.`,
  sameDuration: (id: string, durationMs: number) => `Transition ${id} already lasts ${durationMs} ms.`,
  noCaptions: () => "There are no captions on the timeline.",
  nothing: () => "There is nothing to change.",
  nothingToCut: () => "There is nothing to cut: no span lies on the video.",
  markers: () => `A project holds at most ${MAX_MARKERS} markers.`,
  noMarker: (id: string) => `There is no marker ${id}.`,
  markerTaken: (id: string) => `There is a marker ${id} already.`,
  sameMagnet: (on: boolean) => `The main track magnet is already ${on ? "on" : "off"}.`,
  playheadOutside: (id: string, atMs: number) => `The playhead at ${atMs} ms is not inside clip ${id}.`,
  playheadNearEnd: (id: string, atMs: number) => `A trim at ${atMs} ms leaves a part of clip ${id} shorter than 0.1 s.`,
  volumePoints: (id: string) => `Clip ${id} holds the most volume keyframes it can (${MAX_VOLUME_POINTS}).`,
  noVolumePoint: (id: string, atMs: number) => `Clip ${id} has no volume keyframe at ${atMs} ms.`,
  notSound: (id: string) => `Clip ${id} is not a sound: only a sound has beats.`,
  beatsFull: (id: string) => `Clip ${id} holds the most beats it can (${MAX_BEATS}).`,
  notVideoFile: (id: string) => `Clip ${id} is not a video: only a video's background is removed.`,
  noOriginal: (id: string) => `Clip ${id} has no original to restore: its background was never removed.`,
  syncConflict: () => "Another save changed what this tab changed, and the two cannot both stand.",
} as const;

/**
 * An edit the reducer makes that changes nothing (a setting a clip already has, a trim held where
 * the clip already is): no refusal, but the words for a caller whose rule is that an edit must
 * change something, as the assistant's plan step.
 */
export const UNCHANGED = "That changes nothing: it is already so, or as far as it can go.";

/**
 * The timeline after an edit. An edit the reducer refuses gives back the very same timeline
 * (`refusal` says why), so the undo history keeps no step for it.
 */
export function editTimeline(timeline: Timeline, edit: TimelineEdit): Timeline {
  const next = attempt(timeline, edit);
  return typeof next === "string" ? timeline : next;
}

/** Why the reducer refuses an edit, one of `REFUSALS`; null for an edit it makes (#601). */
export function refusal(timeline: Timeline, edit: TimelineEdit): string | null {
  const next = attempt(timeline, edit);
  return typeof next === "string" ? next : null;
}

/**
 * An edit's outcome: the timeline it makes, its magnetic track packed and its transitions kept on
 * their cuts, or why not. An edit the packing puts back where it was gives the timeline itself.
 */
function attempt(timeline: Timeline, edit: TimelineEdit): Timeline | string {
  // A drag's trims each count from the timeline as the drag found it: the packing and the captions
  // following it would otherwise build on the last move's (spec magnet-ripple decision 8).
  const counted =
    edit.type === "trimStart" || edit.type === "trimEnd" || edit.type === "setSpeed" || edit.type === "setSpeedCurve";
  const base = counted && edit.origin !== undefined ? edit.origin : timeline;
  const next = edited(base, edit);
  // A load is the server's, whose transitions already hold.
  if (typeof next === "string" || next === base || edit.type === "load") return next;
  // A restore is a timeline as it was, `many`'s parts each packed, and a merge holds the main
  // track as both saves left it, or is refused.
  const laid = edit.type === "restore" || edit.type === "many" || edit.type === "sync" ? next : magnetized(base, next, edit);
  if (typeof laid === "string") return laid;
  const done = onCuts(laid);
  // An edit the packing put back where it was (a nudge inside the clip's place) changes nothing.
  return laid !== next && sameParts(base, done) ? base : done;
}

/** The reducer: each guard refuses with its reason where it stands, and nothing refuses otherwise. */
function edited(timeline: Timeline, edit: TimelineEdit): Timeline | string {
  switch (edit.type) {
    case "load":
    case "restore":
      return edit.timeline;
    case "sync":
      return synced(timeline, edit.base, edit.theirs, edit.present ?? timeline);
    case "setAspect":
      return edit.aspect === timeline.aspect ? REFUSALS.sameAspect() : { ...timeline, aspect: edit.aspect };
    case "addClip": {
      if (full(timeline, edit.kind)) return REFUSALS.full(edit.kind);
      const source = edit.source;
      const length = edit.kind === "image" ? STILL_MS : whole(edit.sourceMs ?? 0);
      if (length < MIN_SPAN_MS) return REFUSALS.noLength();
      let next: Timeline;
      let trackId: string;
      let startMs: number;
      if (edit.startMs === undefined) {
        // A video or a picture goes at the magnetic track's end (spec magnet-ripple).
        const main = TRACK_OF[edit.kind] === "video" ? timeline.tracks.find((track) => track.magnet) : undefined;
        [next, trackId] =
          main === undefined ? trackFor(timeline, TRACK_OF[edit.kind], edit.newTrackId) : [timeline, main.id];
        startMs = trackEnd(next, trackId);
      } else {
        startMs = clamp(whole(edit.startMs), 0, MAX_TOTAL_MS - MIN_SPAN_MS);
        const span = Math.min(startMs + length, MAX_TOTAL_MS);
        [next, trackId] = freeTrackAt(timeline, TRACK_OF[edit.kind], edit.id, startMs, span, edit.newTrackId);
      }
      if (trackId === "") return REFUSALS.tracks();
      const endMs = Math.min(startMs + length, MAX_TOTAL_MS);
      if (!fits(startMs, endMs)) return REFUSALS.longest();
      const placed = { id: edit.id, trackId, startMs, endMs, locked: false, hidden: false };
      let clip: Clip;
      if (edit.kind === "image") {
        clip = { ...placed, kind: "image", source, ...PLAIN_LOOK, animations: [] };
      } else if (source.kind === "actor") {
        // An actor is a picture only (live's Avatar shows its portrait).
        return REFUSALS.actor();
      } else if (edit.kind === "video") {
        clip = {
          ...placed,
          kind: "video",
          source,
          inMs: 0,
          ...ONE_SPEED,
          muted: false,
          ...LEVEL,
          ...PLAIN_LOOK,
          animations: [],
          originalSource: null,
        };
      } else {
        clip = { ...placed, kind: "audio", source, inMs: 0, ...ONE_SPEED, muted: false, ...LEVEL, beatsMs: [] };
      }
      return { ...next, clips: [...next.clips, clip] };
    }
    case "addEffect": {
      // Live's: a track of its own on top, from the video's start for live's 5 s.
      if (full(timeline, "effect")) return REFUSALS.full("effect");
      if (timeline.tracks.length >= MAX_TRACKS) return REFUSALS.tracks();
      const track = newTrack(edit.newTrackId, "effect");
      const effect: EffectClip = {
        id: edit.id,
        trackId: track.id,
        kind: "effect",
        effect: edit.effect,
        values: {},
        startMs: 0,
        endMs: STILL_MS,
        locked: false,
        hidden: false,
      };
      return { ...timeline, tracks: [track, ...timeline.tracks], clips: [...timeline.clips, effect] };
    }
    case "addTitle": {
      if (full(timeline, "text")) return REFUSALS.full("text");
      const startMs = clamp(whole(edit.startMs), 0, MAX_TOTAL_MS - MIN_SPAN_MS);
      const endMs = clamp(whole(edit.endMs), startMs + MIN_SPAN_MS, MAX_TOTAL_MS);
      const [next, trackId] = freeTrackAt(timeline, "text", edit.id, startMs, endMs, edit.newTrackId);
      if (trackId === "") return REFUSALS.tracks();
      const title: TextClip = {
        id: edit.id,
        trackId,
        kind: "text",
        text: edit.text,
        style: edit.style,
        animations: [],
        startMs,
        endMs,
        locked: false,
        hidden: false,
      };
      return { ...next, clips: [...next.clips, title] };
    }
    case "moveClip": {
      const clip = changeable(timeline, edit.id);
      if (typeof clip === "string") return clip;
      const trackId = edit.trackId ?? clip.trackId;
      const target = timeline.tracks.find((track) => track.id === trackId);
      if (target === undefined) return REFUSALS.noTrack(trackId);
      if (target.kind !== TRACK_OF[clip.kind]) return REFUSALS.trackKind(clip.id, target.id, target.kind);
      const length = clip.endMs - clip.startMs;
      const startMs = clamp(whole(edit.startMs), 0, MAX_TOTAL_MS - length);
      // On the magnetic track it goes in by its time instead (spec magnet-ripple).
      const over = target.magnet ? undefined : overlapped(timeline, target.id, clip.id, startMs, startMs + length);
      if (over !== undefined) return REFUSALS.overlap(clip.id, over.id);
      const clips = timeline.clips.map((each) =>
        each.id === clip.id ? { ...each, trackId: target.id, startMs, endMs: startMs + length } : each,
      );
      return tidy({ ...timeline, clips });
    }
    case "moveToNewTrack": {
      const clip = changeable(timeline, edit.id);
      if (typeof clip === "string") return clip;
      if (clip.kind === "caption") return REFUSALS.captionStays();
      if (timeline.tracks.some((track) => track.id === edit.newTrackId)) return REFUSALS.trackTaken(edit.newTrackId);
      const own = timeline.tracks.findIndex((track) => track.id === clip.trackId);
      const index = clamp(Math.round(finite(edit.index, 0)), 0, timeline.tracks.length);
      // Alone, its track goes when it leaves, so the stack keeps its count; the magnetic track stays.
      const alone = clipsOn(timeline, clip.trackId, clip.id).length === 0;
      if (alone && (index === own || index === own + 1)) {
        return edited(timeline, { type: "moveClip", id: clip.id, startMs: edit.startMs });
      }
      const frees = alone && !magnetic(timeline, clip.trackId);
      if (!frees && timeline.tracks.length >= MAX_TRACKS) return REFUSALS.tracks();
      const length = clip.endMs - clip.startMs;
      const startMs = clamp(whole(edit.startMs), 0, MAX_TOTAL_MS - length);
      const track = newTrack(edit.newTrackId, TRACK_OF[clip.kind]);
      const tracks = [...timeline.tracks.slice(0, index), track, ...timeline.tracks.slice(index)];
      const clips = timeline.clips.map((each) =>
        each.id === clip.id ? { ...each, trackId: track.id, startMs, endMs: startMs + length } : each,
      );
      return tidy({ ...timeline, tracks, clips });
    }
    case "reorderClip": {
      const clip = changeable(timeline, edit.id);
      if (typeof clip === "string") return clip;
      const onto = timeline.clips.find((each) => each.id === edit.ontoId);
      if (onto === undefined) return REFUSALS.noClip(edit.ontoId);
      if (onto.id === clip.id) return REFUSALS.ontoItself(clip.id);
      // One caption track (spec captions.md decision 8): captions keep their times.
      if (clip.kind === "caption") return REFUSALS.captionStays();
      if (TRACK_OF[clip.kind] !== TRACK_OF[onto.kind]) return REFUSALS.trackKind(clip.id, onto.trackId, TRACK_OF[onto.kind]);
      const length = clip.endMs - clip.startMs;
      const others = clipsOn(timeline, onto.trackId, clip.id);
      const same = clip.trackId === onto.trackId;
      // Earlier on its own track, or from another one: in at its start, the rest later. Later on
      // its own track: in at its end, the clips between closing up earlier.
      const later = !same || onto.startMs < clip.startMs;
      const moved = later
        ? others.filter((each) => each.startMs >= onto.startMs && (!same || each.startMs < clip.startMs))
        : others.filter((each) => each.startMs >= clip.endMs && each.startMs <= onto.startMs);
      const shift = later ? length : -length;
      const blocked = moved.find((each) => each.locked || each.endMs + shift > MAX_TOTAL_MS);
      if (blocked !== undefined) return blocked.locked ? REFUSALS.locked(blocked.id) : REFUSALS.longest();
      const startMs = later ? onto.startMs : onto.endMs - length;
      const shifted = new Set(moved.map((each) => each.id));
      const clips = timeline.clips.map((each) => {
        if (each.id === clip.id) return { ...each, trackId: onto.trackId, startMs, endMs: startMs + length };
        return shifted.has(each.id) ? { ...each, startMs: each.startMs + shift, endMs: each.endMs + shift } : each;
      });
      return tidy({ ...timeline, clips });
    }
    // The source's length bounds only the end: a later start reads a later point of it.
    case "trimStart":
      return mapClip(timeline, edit.id, (clip) => {
        const { low } = room(timeline, clip);
        // A clip shorter than the shortest span (a caption's, #239) has no room to trim.
        if (clip.endMs - MIN_SPAN_MS < low) return REFUSALS.tooShort(clip.id);
        const start = clamp(whole(edit.startMs), low, clip.endMs - MIN_SPAN_MS);
        if (clip.kind === "caption") return captionFrom(clip, start, clip.endMs);
        if (clip.kind === "video" || clip.kind === "audio") {
          // The source cannot be read from before its own start; its in-point moves at its speed.
          const bounded = Math.max(start, Math.ceil(clipMsAt(clip, 0)));
          return { ...clip, startMs: bounded, ...partOf(clip, bounded, clip.endMs) };
        }
        return { ...clip, startMs: start };
      });
    case "trimEnd":
      return mapClip(timeline, edit.id, (clip) => {
        const { high } = room(timeline, clip);
        if (clip.startMs + MIN_SPAN_MS > high) return REFUSALS.tooShort(clip.id);
        let end = clamp(whole(edit.endMs), clip.startMs + MIN_SPAN_MS, high);
        if ((clip.kind === "video" || clip.kind === "audio") && edit.sourceMs !== null) {
          end = Math.min(end, Math.floor(clipMsAt(clip, whole(edit.sourceMs))));
        }
        end = Math.max(end, clip.startMs + MIN_SPAN_MS);
        if (clip.kind === "video" || clip.kind === "audio") return { ...clip, endMs: end, ...partOf(clip, clip.startMs, end) };
        return clip.kind === "caption" ? captionFrom(clip, clip.startMs, end) : { ...clip, endMs: end };
      });
    case "setRange":
      return mapClip(timeline, edit.id, (clip) => {
        if (clip.kind !== "video" && clip.kind !== "audio") return REFUSALS.noFile(clip.id);
        const { high } = room(timeline, clip);
        const sourceEnd = edit.sourceMs === null ? Number.POSITIVE_INFINITY : whole(edit.sourceMs);
        const inMs = clamp(whole(edit.inMs), 0, Math.min(sourceEnd - MIN_SPAN_MS, MAX_FILE_MS));
        const outMs = clamp(whole(edit.outMs), inMs + MIN_SPAN_MS, sourceEnd);
        if (curved(clip)) {
          // The curve laid over the new span, then held to the room before the next clip.
          const laid = laidOver(clip.speedPoints, inMs, outMs - inMs);
          const whole = { ...clip, inMs, speed: laid.speed, endMs: clip.startMs + laid.lengthMs };
          const endMs = Math.max(Math.min(whole.endMs, high), clip.startMs + MIN_SPAN_MS);
          return { ...whole, endMs, ...partOf(whole, clip.startMs, endMs) };
        }
        const endMs = Math.min(clip.startMs + Math.round((outMs - inMs) / clip.speed), high);
        return { ...clip, inMs, endMs: Math.max(endMs, clip.startMs + MIN_SPAN_MS) };
      });
    case "many": {
      // Each as it would be alone: a part refused is left out, and the whole is refused only when
      // every part is, for the first part's reason.
      let next = timeline;
      let made = false;
      let first: string | null = null;
      for (const part of edit.edits) {
        const outcome = attempt(next, part);
        if (typeof outcome === "string") {
          first ??= outcome;
        } else {
          next = outcome;
          made = true;
        }
      }
      return made ? next : (first ?? REFUSALS.nothing());
    }
    case "setClip":
      return mapClip(timeline, edit.id, (clip) => patched(clip, edit.patch));
    case "removeClip": {
      const clip = changeable(timeline, edit.id);
      if (typeof clip === "string") return clip;
      const clips = timeline.clips.filter((each) => each.id !== clip.id);
      // ⇧Delete: the later clips of its track move up by its length. The magnetic track closes up
      // anyway, and a caption goes alone: captions keep their times (captions.md decision 8).
      if (edit.ripple !== true || clip.kind === "caption" || magnetic(timeline, clip.trackId)) {
        return tidy({ ...timeline, clips });
      }
      const later = clipsOn(timeline, clip.trackId, clip.id).filter((each) => each.startMs >= clip.endMs);
      const locked = later.find((each) => each.locked);
      if (locked !== undefined) return REFUSALS.locked(locked.id);
      const moved = new Set(later.map((each) => each.id));
      const length = clipMs(clip);
      return tidy({
        ...timeline,
        clips: clips.map((each) =>
          moved.has(each.id) ? { ...each, startMs: each.startMs - length, endMs: each.endMs - length } : each,
        ),
      });
    }
    case "split": {
      const clip = changeable(timeline, edit.id);
      if (typeof clip === "string") return clip;
      if (full(timeline, clip.kind)) return REFUSALS.full(clip.kind);
      const at = whole(edit.atMs);
      if (at <= clip.startMs || at >= clip.endMs) return REFUSALS.outside(clip.id, at);
      if (at - clip.startMs < MIN_SPAN_MS || clip.endMs - at < MIN_SPAN_MS) return REFUSALS.nearEnd(clip.id, at);
      const second: Clip =
        clip.kind === "video" || clip.kind === "audio"
          ? { ...clip, id: edit.newId, startMs: at, ...partOf(clip, at, clip.endMs) }
          : clip.kind === "caption"
            ? { ...captionFrom(clip, at, clip.endMs, true), id: edit.newId }
            : { ...clip, id: edit.newId, startMs: at };
      const first =
        clip.kind === "caption"
          ? captionFrom(clip, clip.startMs, at)
          : clip.kind === "video" || clip.kind === "audio"
            ? { ...clip, endMs: at, ...partOf(clip, clip.startMs, at) }
            : { ...clip, endMs: at };
      const clips = timeline.clips.flatMap((each) => (each.id === clip.id ? [first, second] : [each]));
      // A transition out of the clip now leaves its second half.
      const transitions = timeline.transitions.map((each) =>
        each.fromId === clip.id ? { ...each, fromId: edit.newId } : each,
      );
      return { ...timeline, clips, transitions };
    }
    case "duplicate": {
      // A lock keeps that clip as it is, not its copies: a locked clip is duplicated.
      const clip = timeline.clips.find((each) => each.id === edit.id);
      if (clip === undefined) return REFUSALS.noClip(edit.id);
      // One caption track (spec captions.md decision 8): a caption is not duplicated.
      if (clip.kind === "caption") return REFUSALS.captionCopy();
      if (full(timeline, clip.kind)) return REFUSALS.full(clip.kind);
      if (timeline.tracks.length >= MAX_TRACKS) return REFUSALS.tracks();
      const at = timeline.tracks.findIndex((track) => track.id === clip.trackId);
      const track = newTrack(edit.newTrackId, TRACK_OF[clip.kind]);
      const tracks = [...timeline.tracks.slice(0, at), track, ...timeline.tracks.slice(at)];
      // A lock keeps that clip as it is, not its copies.
      const copy = { ...clip, id: edit.newId, trackId: track.id, locked: false };
      return { ...timeline, tracks, clips: [...timeline.clips, copy] };
    }
    case "separateAudio": {
      const video = changeable(timeline, edit.id);
      if (typeof video === "string") return video;
      if (video.kind !== "video") return REFUSALS.notVideo(video.id);
      if (full(timeline, "audio")) return REFUSALS.full("audio");
      const { startMs, endMs } = video;
      const free = timeline.tracks.find(
        (track) => track.kind === "audio" && !overlaps(timeline, track.id, edit.newId, startMs, endMs),
      );
      if (free === undefined && timeline.tracks.length >= MAX_TRACKS) return REFUSALS.tracks();
      const tracks: readonly Track[] =
        free === undefined
          ? [...timeline.tracks, newTrack(edit.newTrackId, "audio")]
          : timeline.tracks;
      const sound: AudioClip = {
        id: edit.newId,
        trackId: free?.id ?? edit.newTrackId,
        kind: "audio",
        source: video.source,
        inMs: video.inMs,
        speed: video.speed,
        speedPoints: video.speedPoints,
        speedCurve: video.speedCurve,
        startMs,
        endMs,
        locked: false,
        hidden: false,
        muted: false,
        volume: video.volume,
        volumePoints: video.volumePoints,
        fadeInMs: video.fadeInMs,
        fadeOutMs: video.fadeOutMs,
        beatsMs: [],
      };
      const clips = timeline.clips.map((each) =>
        each.id === video.id ? { ...video, muted: true } : each,
      );
      return { ...timeline, tracks, clips: [...clips, sound] };
    }
    case "setLock": {
      // The one edit a locked clip takes.
      const clip = timeline.clips.find((each) => each.id === edit.id);
      if (clip === undefined) return REFUSALS.noClip(edit.id);
      if (clip.locked === edit.locked) return edit.locked ? REFUSALS.alreadyLocked(clip.id) : REFUSALS.notLocked(clip.id);
      return {
        ...timeline,
        clips: timeline.clips.map((each) => (each.id === clip.id ? { ...each, locked: edit.locked } : each)),
      };
    }
    case "paste":
      return pasted(timeline, edit);
    case "cutSpans":
      return cut(timeline, edit);
    case "replaceCaptions":
      return withCaptions(timeline, edit.captions, edit.newTrackId);
    case "setCaptions": {
      const boxes = edit.boxes;
      const styled: Timeline = {
        ...timeline,
        captions: edit.captions,
        clips:
          boxes === undefined
            ? timeline.clips
            : timeline.clips.map((clip) =>
                clip.kind === "caption" && !clip.locked ? { ...clip, box: boxWithin(boxes(clip)) } : clip,
              ),
      };
      if (edit.cut === null) return styled;
      // A re-cut refused (a caption locked, past the counts) refuses the look too.
      const recut = recutCaptions(timeline, edit.captions, edit.cut, edit.idPrefix);
      if (typeof recut === "string") return recut;
      const next = withCaptions(styled, recut, edit.newTrackId);
      // With no caption to cut and no room for their track, the look alone changes.
      return typeof next === "string" ? styled : next;
    }
    case "removeCaptions": {
      const clips = timeline.clips.filter((clip) => clip.kind !== "caption");
      return clips.length === timeline.clips.length ? REFUSALS.noCaptions() : { ...timeline, clips };
    }
    case "setCaptionWords":
      return mapClip(timeline, edit.id, (clip) =>
        clip.kind === "caption" ? { ...clip, words: wordsWithin(edit.words, clipMs(clip)) } : REFUSALS.notCaption(clip.id),
      );
    case "splitCaption": {
      const clip = changeable(timeline, edit.id);
      if (typeof clip === "string") return clip;
      if (clip.kind !== "caption") return REFUSALS.notCaption(clip.id);
      if (full(timeline, "caption")) return REFUSALS.full("caption");
      const at = clip.startMs + whole(edit.atMs);
      if (at <= clip.startMs || at >= clip.endMs) return REFUSALS.outside(clip.id, at);
      const first: CaptionClip = { ...clip, endMs: at, words: wordsWithin(edit.first, at - clip.startMs) };
      const second: CaptionClip = {
        ...clip,
        id: edit.newId,
        startMs: at,
        words: wordsWithin(edit.second, clip.endMs - at),
      };
      const clips = timeline.clips.flatMap((each) => (each.id === clip.id ? [first, second] : [each]));
      return { ...timeline, clips };
    }
    case "setTransition": {
      const cut = junctionsOf(timeline).find(
        (each) => each.fromId === edit.fromId && each.toId === edit.toId,
      );
      if (cut === undefined) return REFUSALS.noCut(edit.fromId, edit.toId);
      if (cut.transition !== null) {
        if (cut.transition.kind === edit.kind) return REFUSALS.sameTransition(edit.fromId, edit.toId);
        const changed = { ...cut.transition, kind: edit.kind };
        return withTransition(timeline, changed);
      }
      const durationMs = Math.min(TRANSITION_MS, cut.limitMs);
      const added = { id: edit.id, fromId: edit.fromId, toId: edit.toId, kind: edit.kind, durationMs };
      return { ...timeline, transitions: [...timeline.transitions, added] };
    }
    case "setTransitionDuration": {
      const cut = junctionsOf(timeline).find((each) => each.transition?.id === edit.id);
      if (cut?.transition == null) return REFUSALS.noTransition(edit.id);
      const durationMs = clamp(whole(edit.durationMs), MIN_TRANSITION_MS, cut.limitMs);
      if (durationMs === cut.transition.durationMs) return REFUSALS.sameDuration(edit.id, durationMs);
      return withTransition(timeline, { ...cut.transition, durationMs });
    }
    case "removeTransition": {
      const transitions = timeline.transitions.filter((each) => each.id !== edit.id);
      return transitions.length === timeline.transitions.length ? REFUSALS.noTransition(edit.id) : { ...timeline, transitions };
    }
    case "setMagnet": {
      const main = timeline.tracks.find((track) => track.magnet);
      if ((main !== undefined) === edit.on) return REFUSALS.sameMagnet(edit.on);
      const set = (chosen: Track | undefined, magnet: boolean): Timeline => ({
        ...timeline,
        tracks: timeline.tracks.map((track) => (track === chosen ? { ...track, magnet } : track)),
      });
      if (!edit.on) return set(main, false);
      // D6: the bottom video track, CapCut's main track; a new one there when there is none.
      const bottom = timeline.tracks.findLast((track) => track.kind === "video");
      if (bottom !== undefined) return set(bottom, true);
      if (timeline.tracks.length >= MAX_TRACKS) return REFUSALS.tracks();
      return { ...timeline, tracks: placeTrack(timeline.tracks, { ...newTrack(edit.newTrackId, "video"), magnet: true }) };
    }
    case "trimToPlayhead": {
      const clip = changeable(timeline, edit.id);
      if (typeof clip === "string") return clip;
      const at = whole(edit.atMs);
      if (at <= clip.startMs || at >= clip.endMs) return REFUSALS.playheadOutside(clip.id, at);
      if ((edit.side === "start" ? clip.endMs - at : at - clip.startMs) < MIN_SPAN_MS) {
        return REFUSALS.playheadNearEnd(clip.id, at);
      }
      return edited(
        timeline,
        edit.side === "start"
          ? { type: "trimStart", id: clip.id, startMs: at }
          : { type: "trimEnd", id: clip.id, endMs: at, sourceMs: null },
      );
    }
    case "setSpeed": {
      const clip = changeable(timeline, edit.id);
      if (typeof clip === "string") return clip;
      if (clip.kind !== "video" && clip.kind !== "audio") return REFUSALS.noFile(clip.id);
      const speed = clamp(Math.round(finite(edit.speed, clip.speed) * 100) / 100, MIN_SPEED, MAX_SPEED);
      const endMs = clip.startMs + Math.max(MIN_SPAN_MS, Math.round((clipMs(clip) * clip.speed) / speed));
      // A curve is taken away; a clip with none keeps its own (`sameFields` compares them).
      return resped(timeline, clip, { speed, endMs, ...(curved(clip) ? NO_CURVE : {}) }, edit.ripple);
    }
    case "setSpeedCurve": {
      const clip = changeable(timeline, edit.id);
      if (typeof clip === "string") return clip;
      if (clip.kind !== "video" && clip.kind !== "audio") return REFUSALS.noFile(clip.id);
      // Its file span, in whole milliseconds, is kept: the curve sets how long it takes.
      const spanMs = spanOf(clip);
      if (edit.curve === null) {
        if (!curved(clip)) return timeline;
        return resped(timeline, clip, { ...ONE_SPEED, endMs: clip.startMs + Math.max(MIN_SPAN_MS, spanMs) }, edit.ripple);
      }
      const points = heldSpeedPoints(edit.curve.points, MAX_FILE_MS, clip.inMs, clip.inMs + spanMs);
      const { lengthMs, speed } = laidOver(points, clip.inMs, spanMs);
      const changes = { speed, speedPoints: points, speedCurve: edit.curve.name, endMs: clip.startMs + lengthMs };
      return resped(timeline, clip, changes, edit.ripple);
    }
    case "setVolumeAt":
    case "addVolumePoint":
    case "removeVolumePoint": {
      const clip = changeable(timeline, edit.id);
      if (typeof clip === "string") return clip;
      if (clip.kind !== "video" && clip.kind !== "audio") return REFUSALS.noFile(clip.id);
      // With no keyframes the slider is the clip's one level, wherever the playhead is.
      if (edit.type === "setVolumeAt" && clip.volumePoints.length === 0) {
        const volume = clamp(finite(edit.volume, clip.volume), 0, MAX_VOLUME);
        return mapClip(timeline, clip.id, () => ({ ...clip, volume }));
      }
      const at = whole(edit.atMs);
      if (at < clip.startMs || at > clip.endMs) return REFUSALS.playheadOutside(clip.id, at);
      const onPoint = volumePointNear(clip, at);
      const near = onPoint === undefined ? -1 : clip.volumePoints.indexOf(onPoint);
      if (edit.type === "removeVolumePoint") {
        if (near < 0) return REFUSALS.noVolumePoint(clip.id, at);
        const kept = clip.volumePoints.filter((_, index) => index !== near);
        // The last keyframe taken off leaves the clip flat at its level, so nothing jumps.
        const volume = kept.length === 0 ? clip.volumePoints[near]!.volume : clip.volume;
        return mapClip(timeline, clip.id, () => ({ ...clip, volumePoints: kept, volume }));
      }
      const volume = edit.type === "setVolumeAt" ? clamp(finite(edit.volume, clip.volume), 0, MAX_VOLUME) : volumeAt(clip, at);
      if (near >= 0) {
        if (edit.type === "addVolumePoint") return mapClip(timeline, clip.id, () => clip);
        const volumePoints = clip.volumePoints.map((point, index) => (index === near ? { ...point, volume } : point));
        return mapClip(timeline, clip.id, () => ({ ...clip, volumePoints }));
      }
      if (clip.volumePoints.length >= MAX_VOLUME_POINTS) return REFUSALS.volumePoints(clip.id);
      const added: VolumePoint = { atMs: readsAt(clip, at), volume };
      const volumePoints = [...clip.volumePoints, added].sort((one, other) => one.atMs - other.atMs);
      return mapClip(timeline, clip.id, () => ({ ...clip, volumePoints }));
    }
    case "addMarker": {
      if (timeline.markers.length >= MAX_MARKERS) return REFUSALS.markers();
      if (timeline.markers.some((each) => each.id === edit.id)) return REFUSALS.markerTaken(edit.id);
      const label = labelOf(edit.label) ?? MARKER_LABEL;
      const color = colourOr(edit.color ?? MARKER_COLORS[0], MARKER_COLORS[0]);
      const added: Marker = { id: edit.id, atMs: markerTime(edit.atMs), label, color };
      return { ...timeline, markers: byTime([...timeline.markers, added]) };
    }
    case "setMarker": {
      const marker = timeline.markers.find((each) => each.id === edit.id);
      if (marker === undefined) return REFUSALS.noMarker(edit.id);
      const { atMs, label, color } = edit.patch;
      const changed: Marker = {
        ...marker,
        atMs: atMs === undefined ? marker.atMs : markerTime(atMs),
        label: labelOf(label) ?? marker.label,
        color: colourOr(color ?? marker.color, marker.color),
      };
      return { ...timeline, markers: byTime(timeline.markers.map((each) => (each.id === marker.id ? changed : each))) };
    }
    case "removeMarker": {
      const markers = timeline.markers.filter((each) => each.id !== edit.id);
      return markers.length === timeline.markers.length ? REFUSALS.noMarker(edit.id) : { ...timeline, markers };
    }
    case "removeBackground":
      return mapClip(timeline, edit.id, (clip) =>
        clip.kind === "video"
          ? { ...clip, source: edit.source, originalSource: clip.originalSource ?? clip.source, chromaKey: BACKGROUND_KEY }
          : REFUSALS.notVideoFile(clip.id),
      );
    case "restoreOriginal":
      return mapClip(timeline, edit.id, (clip) => {
        if (clip.kind !== "video" || clip.originalSource === null) return REFUSALS.noOriginal(clip.id);
        return { ...clip, source: clip.originalSource, originalSource: null, chromaKey: null };
      });
    case "setBeats":
      return mapClip(timeline, edit.id, (clip) =>
        clip.kind === "audio" ? { ...clip, beatsMs: beatsKept(clip, edit.beatsMs) } : REFUSALS.notSound(clip.id),
      );
    case "toggleBeat":
      return mapClip(timeline, edit.id, (clip) => {
        if (clip.kind !== "audio") return REFUSALS.notSound(clip.id);
        const at = whole(edit.atMs);
        if (!showsAt(clip, at)) return REFUSALS.playheadOutside(clip.id, at);
        const near = nearestBeat(clip, at);
        if (near !== null) return { ...clip, beatsMs: clip.beatsMs.filter((beat) => beat !== near) };
        if (clip.beatsMs.length >= MAX_BEATS) return REFUSALS.beatsFull(clip.id);
        return { ...clip, beatsMs: beatList([...clip.beatsMs, readsAt(clip, at)]) };
      });
    default:
      return edit satisfies never;
  }
}

/** The spans held to the video, at least 1 ms each, earliest first, those that touch merged. */
function mergedSpans(spans: readonly Span[], totalMs: number): Span[] {
  const held = spans
    .map((span) => ({ startMs: clamp(whole(span.startMs), 0, totalMs), endMs: clamp(whole(span.endMs), 0, totalMs) }))
    .filter((span) => span.endMs > span.startMs)
    .sort((one, other) => one.startMs - other.startMs);
  const merged: Span[] = [];
  for (const span of held) {
    const last = merged.at(-1);
    if (last !== undefined && span.startMs <= last.endMs) last.endMs = Math.max(last.endMs, span.endMs);
    else merged.push(span);
  }
  return merged;
}

/** How much of the spans lies before a time: how far a moment after them moves earlier. */
function cutBefore(spans: readonly Span[], atMs: number): number {
  return spans.reduce((sum, span) => sum + Math.max(0, Math.min(span.endMs, atMs) - span.startMs), 0);
}

/** A clip's parts outside the spans, on the video's time as it was. */
function partsOutside(clip: Clip, spans: readonly Span[]): Span[] {
  const parts: Span[] = [];
  let from = clip.startMs;
  for (const span of spans) {
    if (span.endMs <= from || span.startMs >= clip.endMs) continue;
    if (span.startMs > from) parts.push({ startMs: from, endMs: span.startMs });
    from = Math.max(from, span.endMs);
  }
  if (from < clip.endMs) parts.push({ startMs: from, endMs: clip.endMs });
  return parts;
}

/** The timeline with the spans taken out and closed up, or why not (a `cutSpans`). */
function cut(timeline: Timeline, edit: Extract<TimelineEdit, { type: "cutSpans" }>): Timeline | string {
  const spans = mergedSpans(edit.spans, totalMs(timeline));
  if (spans.length === 0) return REFUSALS.nothingToCut();
  const clips: Clip[] = [];
  // Each cut clip's last part, which a transition out of it now leaves (as `split`).
  const lastPart = new Map<string, string>();
  let made = 0;
  for (const clip of timeline.clips) {
    const parts = partsOutside(clip, spans);
    const untouched = parts.length === 1 && parts[0]?.startMs === clip.startMs && parts[0].endMs === clip.endMs;
    if (untouched && cutBefore(spans, clip.startMs) === 0) {
      clips.push(clip);
      continue;
    }
    // A lock keeps a clip as it is: cut or moved earlier, the whole cut is refused.
    if (clip.locked) return REFUSALS.locked(clip.id);
    // A sliver under the shortest span goes; a caption may be as short as 1 ms (#239).
    const shortest = clip.kind === "caption" ? 1 : MIN_SPAN_MS;
    const kept = parts.filter((each) => each.endMs - each.startMs >= shortest);
    for (const [at, part] of kept.entries()) {
      // The clip's first part keeps its id, so a transition into it stays.
      const id = at === 0 ? clip.id : `${edit.idPrefix}-${made++}`;
      const shift = cutBefore(spans, part.startMs);
      const placed = { id, startMs: part.startMs - shift, endMs: part.endMs - shift };
      if (clip.kind === "caption") {
        clips.push({ ...captionFrom(clip, part.startMs, part.endMs, part.startMs > clip.startMs), ...placed });
      } else if (clip.kind === "video" || clip.kind === "audio") {
        clips.push({ ...clip, ...placed, ...partOf(clip, part.startMs, part.endMs) });
      } else {
        clips.push({ ...clip, ...placed });
      }
      lastPart.set(clip.id, id);
    }
  }
  const next: Timeline = {
    ...timeline,
    clips,
    transitions: timeline.transitions.map((each) => {
      const last = lastPart.get(each.fromId);
      return last === undefined ? each : { ...each, fromId: last };
    }),
  };
  const over = (["video", "audio", "text", "effect", "caption"] as const).find(
    (kind) => countOf(next, kind) > KIND_LIMIT[group(kind)],
  );
  return over === undefined ? tidy(next) : REFUSALS.full(over);
}

/**
 * Whether the captions follow the magnetic track after an edit (spec magnet-ripple decision 7):
 * after each edit that can move its clips, not after a split, which moves nothing, nor after one
 * that places the captions itself.
 */
const CAPTIONS_FOLLOW: Record<TimelineEdit["type"], boolean> = {
  load: false,
  restore: false,
  sync: false,
  setAspect: false,
  addClip: true,
  addEffect: false,
  addTitle: false,
  moveClip: true,
  moveToNewTrack: true,
  reorderClip: true,
  trimStart: true,
  trimEnd: true,
  setRange: true,
  setClip: false,
  removeClip: true,
  split: false,
  duplicate: false,
  separateAudio: false,
  setLock: false,
  replaceCaptions: false,
  removeCaptions: false,
  setCaptions: false,
  setCaptionWords: false,
  splitCaption: false,
  many: false,
  setTransition: false,
  setTransitionDuration: false,
  removeTransition: false,
  paste: true,
  cutSpans: false,
  setMagnet: true,
  trimToPlayhead: true,
  setSpeed: true,
  setSpeedCurve: true,
  setVolumeAt: false,
  addVolumePoint: false,
  removeVolumePoint: false,
  addMarker: false,
  setMarker: false,
  removeMarker: false,
  toggleBeat: false,
  removeBackground: false,
  restoreOriginal: false,
  setBeats: false,
};

/** The timeline after an edit, its magnetic track packed and the captions following it. */
function magnetized(before: Timeline, after: Timeline, edit: TimelineEdit): Timeline | string {
  const main = after.tracks.find((track) => track.magnet);
  if (main === undefined) return after;
  const laid = packed(before, after, main.id, edit);
  return typeof laid === "string" || !CAPTIONS_FOLLOW[edit.type] ? laid : followed(before, laid, main.id);
}

/** The clips an edit placed at a time of its choosing: on the magnetic track they go in by it. */
function placedBy(edit: TimelineEdit): ReadonlySet<string> {
  switch (edit.type) {
    case "moveClip":
    case "moveToNewTrack":
      return new Set([edit.id]);
    case "addClip":
      return new Set(edit.startMs === undefined ? [] : [edit.id]);
    case "paste":
      return new Set(edit.newIds);
    default:
      return new Set();
  }
}

/** `items` into `list` at `at`, or at its end for -1. */
function insertAt<T>(list: T[], at: number, items: readonly T[]): void {
  list.splice(at === -1 ? list.length : at, 0, ...items);
}

/**
 * The magnetic track's clips in the order the packing lays them (spec magnet-ripple). Those that
 * were on it keep their order; one new to it that the edit did not place (a split's second part,
 * the bin's Add) goes by its start; the placed ones go in together before the first other clip
 * whose middle, as it lay before the edit, comes after the first one's time. A reorder's own
 * starts already make room.
 */
function packOrder(before: Timeline, after: Timeline, trackId: string, edit: TimelineEdit): Clip[] {
  const on = clipsOn(after, trackId);
  if (edit.type === "reorderClip") return on;
  const placed = placedBy(edit);
  const rank = new Map(clipsOn(before, trackId).map((clip, at) => [clip.id, at]));
  const order = on
    .filter((clip) => rank.has(clip.id) && !placed.has(clip.id))
    .sort((one, other) => (rank.get(one.id) ?? 0) - (rank.get(other.id) ?? 0));
  for (const clip of on.filter((each) => !rank.has(each.id) && !placed.has(each.id))) {
    insertAt(order, order.findIndex((other) => other.startMs > clip.startMs), [clip]);
  }
  const going = on.filter((clip) => placed.has(clip.id));
  const first = going[0];
  if (first !== undefined) {
    const was = new Map(before.clips.map((clip) => [clip.id, clip]));
    const middle = (clip: Clip) => {
      const lay = was.get(clip.id) ?? clip;
      return (lay.startMs + lay.endMs) / 2;
    };
    insertAt(order, order.findIndex((other) => middle(other) > first.startMs), going);
  }
  return order;
}

/**
 * The magnetic track laid back to back from 0, in `packOrder`'s order. Refused when a locked clip
 * would move (a lock keeps a clip where it is, decision 5) or past the longest video. A clip laid
 * back where it was before is that clip itself.
 */
function packed(before: Timeline, after: Timeline, trackId: string, edit: TimelineEdit): Timeline | string {
  const was = new Map(before.clips.map((clip) => [clip.id, clip]));
  const moved = new Map<string, Clip>();
  let atMs = 0;
  for (const clip of packOrder(before, after, trackId, edit)) {
    const endMs = atMs + clipMs(clip);
    if (clip.startMs !== atMs) {
      if (clip.locked) return REFUSALS.locked(clip.id);
      const laid = { ...clip, startMs: atMs, endMs };
      const prior = was.get(clip.id);
      moved.set(clip.id, prior !== undefined && sameFields(prior, laid) ? prior : laid);
    }
    atMs = endMs;
  }
  if (atMs > MAX_TOTAL_MS) return REFUSALS.longest();
  return moved.size === 0 ? after : { ...after, clips: after.clips.map((clip) => moved.get(clip.id) ?? clip) };
}

/** Time that does not move. */
const STILL: Move = { scale: 1, shift: 0 };

/**
 * How a time maps after an edit: `t′ = scale × t + shift` (a speed's change scales, spec clip-speed),
 * or `through` a clip's curves when its curve changed (spec clip-speed-curves).
 */
interface Move {
  scale: number;
  shift: number;
  through?: (atMs: number) => number;
}

/** Where a time lands after a move. */
function movedTo(move: Move, atMs: number): number {
  return move.through === undefined ? move.scale * atMs + move.shift : move.through(atMs);
}

/** Whether two moves are one: the same line, or the same curve's. */
function sameMove(one: Move, other: Move): boolean {
  return one === other || (one.through === undefined && other.through === undefined && one.scale === other.scale && one.shift === other.shift);
}

/** A span of the video's time before an edit, and how its content moved: null if taken off. */
interface Piece {
  startMs: number;
  endMs: number;
  move: Move | null;
}

/** The point of its file a clip shows at its start: a video's or a sound's in-point, else 0. */
function readsFrom(clip: Clip): number {
  return clip.kind === "video" || clip.kind === "audio" ? clip.inMs : 0;
}

/** How fast a clip reads its file: a video's or a sound's speed, else 1. */
function paceOf(clip: Clip): number {
  return clip.kind === "video" || clip.kind === "audio" ? clip.speed : 1;
}

/**
 * How a clip's content moved in an edit, `was` to `is` (one id): a time t of `was` shows file time
 * f, which `is` shows at t′. One line through both while neither plays a curve, or while its curve
 * is the one it had (moved, trimmed); else through the two curves' time maps (spec clip-speed-curves).
 */
function moveOf(was: Clip, is: Clip): Move {
  const sped = (was.kind === "video" || was.kind === "audio") && (is.kind === "video" || is.kind === "audio");
  if (sped && (curved(was) || curved(is))) {
    return was.speedPoints === is.speedPoints
      ? { scale: 1, shift: is.startMs - clipMsAt(was, is.inMs) }
      : { scale: 1, shift: 0, through: (atMs) => clipMsAt(is, fileMsAt(was, atMs)) };
  }
  const scale = paceOf(was) / paceOf(is);
  return { scale, shift: is.startMs + (readsFrom(was) - was.startMs * paceOf(was) - readsFrom(is)) / paceOf(is) };
}

/** The times of `was` that `is` still shows (one id, moved by `move`): where its content now begins and ends. */
function shownOf(was: Clip, is: Clip, move: Move): { fromMs: number; toMs: number } {
  const sped = (was.kind === "video" || was.kind === "audio") && (is.kind === "video" || is.kind === "audio");
  if (sped && move.through !== undefined) {
    const fromMs = clamp(clipMsAt(was, is.inMs), was.startMs, was.endMs);
    return { fromMs, toMs: clamp(clipMsAt(was, fileMsAt(is, is.endMs)), fromMs, was.endMs) };
  }
  const fromMs = clamp((is.startMs - move.shift) / move.scale, was.startMs, was.endMs);
  return { fromMs, toMs: clamp((is.endMs - move.shift) / move.scale, fromMs, was.endMs) };
}

/**
 * Where the magnetic track's content went (decision 7), on the time before the edit: a clip by its
 * id, a video by the point of its file each time shows. What is no longer shown there was taken
 * off, as was a gap the packing closed and a clip that left the track. After the track's end, time
 * moves with its last clip's content while that clip stays last, as a caption running past it does;
 * else it goes, as nothing of the track follows there.
 */
function piecesOf(before: Timeline, after: Timeline, trackId: string): Piece[] {
  const now = new Map(after.clips.map((clip) => [clip.id, clip]));
  const pieces: Piece[] = [];
  let reached = 0;
  let last: Move | null = null;
  for (const was of clipsOn(before, trackId)) {
    pieces.push({ startMs: reached, endMs: was.startMs, move: null });
    reached = was.endMs;
    const is = now.get(was.id);
    if (is?.trackId !== trackId) {
      pieces.push({ startMs: was.startMs, endMs: was.endMs, move: null });
      last = null;
      continue;
    }
    const move = moveOf(was, is);
    const { fromMs: from, toMs: to } = shownOf(was, is, move);
    pieces.push(
      { startMs: was.startMs, endMs: from, move: null },
      { startMs: from, endMs: to, move },
      { startMs: to, endMs: was.endMs, move: null },
    );
    last = move;
  }
  const stays = clipsOn(after, trackId).at(-1)?.id === clipsOn(before, trackId).at(-1)?.id;
  pieces.push({ startMs: reached, endMs: Number.POSITIVE_INFINITY, move: stays ? last : null });
  return pieces.filter((piece) => piece.endMs > piece.startMs);
}

/**
 * A caption's parts by the pieces it crosses, on the time before the edit: those taken off left
 * out, each run of one shift whole. A part over no piece keeps its time.
 */
function partsOf(caption: Clip, pieces: readonly Piece[]): { startMs: number; endMs: number; move: Move }[] {
  const edges = [...new Set([caption.startMs, caption.endMs, ...pieces.flatMap((each) => [each.startMs, each.endMs])])]
    .filter((at) => at >= caption.startMs && at <= caption.endMs)
    .sort((one, other) => one - other);
  const parts: { startMs: number; endMs: number; move: Move }[] = [];
  for (const [at, startMs] of edges.entries()) {
    const endMs = edges[at + 1];
    if (endMs === undefined) break;
    const piece = pieces.find((each) => each.startMs <= startMs && endMs <= each.endMs);
    const move = piece === undefined ? STILL : piece.move;
    if (move === null) continue;
    const last = parts.at(-1);
    if (last?.endMs === startMs && sameMove(last.move, move)) last.endMs = endMs;
    else parts.push({ startMs, endMs, move });
  }
  return parts;
}

/**
 * The clips that left the magnetic track for another (D7, spec captions-move-with-clip), each with
 * its span before the edit and how its content moved: a caption wholly inside one holds that
 * clip's words, so it goes with it. A clip removed is none: its words are gone.
 */
function carriersOf(before: Timeline, after: Timeline, trackId: string): { startMs: number; endMs: number; move: Move }[] {
  const now = new Map(after.clips.map((clip) => [clip.id, clip]));
  return clipsOn(before, trackId).flatMap((was) => {
    const is = now.get(was.id);
    return is === undefined || is.trackId === trackId ? [] : [{ startMs: was.startMs, endMs: was.endMs, move: moveOf(was, is) }];
  });
}

/**
 * The captions an edit took off with a clip that left the magnetic track (D7, spec
 * captions-move-with-clip): wholly inside it before and gone after, as they would have lain over
 * another caption. The editor names them in a notice, and Undo brings them back.
 */
export function captionsDropped(before: Timeline, after: Timeline): CaptionClip[] {
  const main = before.tracks.find((track) => track.magnet);
  const carriers = main === undefined ? [] : carriersOf(before, after, main.id);
  if (carriers.length === 0) return [];
  const kept = new Set(after.clips.map((clip) => clip.id));
  return before.clips.filter(
    (clip): clip is CaptionClip =>
      clip.kind === "caption" && !kept.has(clip.id) && carriers.some((each) => each.startMs <= clip.startMs && clip.endMs <= each.endMs),
  );
}

/** A caption part's id: its caption's with the first number free, within the contract's 64 characters. */
function freeId(base: string, taken: ReadonlySet<string>): string {
  for (let n = 1; ; n += 1) {
    const suffix = `-${n}`;
    const id = `${base.slice(0, 64 - suffix.length)}${suffix}`;
    if (!taken.has(id)) return id;
  }
}

/**
 * The captions after the magnetic track moved (decision 7): each part moves with the content it
 * lay over, a part over content taken off goes, and a caption whose parts no longer meet is cut in
 * two, its words kept in time. They follow as the track's words only: when one starts over no clip
 * of it, the moved ones could land on it, so all keep their times. Either way, a caption wholly
 * inside a clip that left the track for another moves whole with that clip (D7), and is taken off
 * where it would lie over another caption, the clip moving still; one across two clips follows the
 * track. Refused for a locked caption that would change or go, past the longest video, over
 * another caption or past the captions' count.
 */
function followed(before: Timeline, after: Timeline, trackId: string): Timeline | string {
  const captions = after.clips.filter((clip) => clip.kind === "caption");
  const pieces = piecesOf(before, after, trackId);
  if (captions.length === 0 || pieces.every((piece) => piece.move !== null && sameMove(piece.move, STILL))) return after;
  const main = clipsOn(before, trackId);
  const follows = captions.every((caption) => main.some((each) => showsAt(each, caption.startMs)));
  const carriers = carriersOf(before, after, trackId);
  if (!follows && carriers.length === 0) return after;
  const taken = new Set(after.clips.map((clip) => clip.id));
  const clips: Clip[] = [];
  // The captions a clip that left the track carries, those that stay in place too.
  const carried = new Set<string>();
  for (const clip of after.clips) {
    if (clip.kind !== "caption") {
      clips.push(clip);
      continue;
    }
    const carrier = carriers.find((each) => each.startMs <= clip.startMs && clip.endMs <= each.endMs);
    if (carrier !== undefined) carried.add(clip.id);
    const one = (move: Move) => [{ startMs: clip.startMs, endMs: clip.endMs, move }];
    const parts = carrier !== undefined ? one(carrier.move) : follows ? partsOf(clip, pieces) : one(STILL);
    const [only] = parts;
    const still = only !== undefined && sameMove(only.move, STILL);
    if (parts.length === 1 && still && only?.startMs === clip.startMs && only.endMs === clip.endMs) {
      clips.push(clip);
      continue;
    }
    if (clip.locked) return REFUSALS.locked(clip.id);
    for (const [at, part] of parts.entries()) {
      const id = at === 0 ? clip.id : freeId(clip.id, taken);
      taken.add(id);
      const cut = captionFrom(clip, part.startMs, part.endMs, part.startMs > clip.startMs);
      const { move } = part;
      const startMs = Math.round(movedTo(move, part.startMs));
      const endMs = Math.max(startMs + 1, Math.round(movedTo(move, part.endMs)));
      // A speed's change scales its words in time too; a curve's moves each to the moment it is heard.
      const heard = (wordMs: number) =>
        move.through === undefined ? Math.round(wordMs * move.scale) : Math.round(move.through(part.startMs + wordMs)) - startMs;
      const words = cut.words.map((word) => ({ ...word, startMs: heard(word.startMs), endMs: heard(word.endMs) }));
      clips.push({ ...cut, id, startMs, endMs, words: wordsWithin(words, endMs - startMs) });
    }
  }
  // D7: a carried caption that would lie over another is taken off, and the clip moves still; a
  // locked one stays where it is, so the edit is refused.
  const others = clips.filter((clip) => clip.kind === "caption" && !carried.has(clip.id));
  const crowded = (clip: Clip) =>
    carried.has(clip.id) && others.some((other) => other.startMs < clip.endMs && clip.startMs < other.endMs);
  const pinned = clips.find((clip) => crowded(clip) && clip.locked);
  if (pinned !== undefined) return REFUSALS.locked(pinned.id);
  const kept = clips.filter((clip) => !crowded(clip));
  const next = { ...after, clips: kept };
  // A caption moved past the longest video, or over another, refuses the edit, as a clip would.
  if (kept.some((clip) => clip.kind === "caption" && clip.endMs > MAX_TOTAL_MS)) return REFUSALS.longest();
  const laid = kept.filter((clip) => clip.kind === "caption").sort((one, other) => one.startMs - other.startMs);
  const over = laid.findIndex((clip, at) => at > 0 && clip.startMs < (laid[at - 1]?.endMs ?? 0));
  const [under, top] = [laid[over - 1], laid[over]];
  if (under !== undefined && top !== undefined) return REFUSALS.overlap(top.id, under.id);
  return countOf(next, "caption") > KIND_LIMIT.caption ? REFUSALS.full("caption") : next;
}

/** Whether two values hold the same, however deep, whatever their keys' order. */
function alike(one: unknown, other: unknown): boolean {
  if (one === other) return true;
  if (typeof one !== "object" || typeof other !== "object" || one === null || other === null) return false;
  if (Array.isArray(one) !== Array.isArray(other)) return false;
  const first = one as Record<string, unknown>;
  const second = other as Record<string, unknown>;
  const keys = Object.keys(first);
  return keys.length === Object.keys(second).length && keys.every((key) => key in second && alike(first[key], second[key]));
}

/**
 * A clip's fields merged as one (spec live-sync-merge.md, decisions 1 and 8): where and how it
 * plays, its speed curve with it, which sets its length and speed (spec clip-speed-curves), and a
 * caption's words, which are timed in its span, so a trim and the words it kept go together.
 */
const PLACEMENT: ReadonlySet<string> = new Set(["trackId", "startMs", "endMs", "inMs", "speed", "speedPoints", "speedCurve", "words"]);

/**
 * One value merged (spec live-sync-merge.md, decision 5): theirs where this step holds base's; this
 * step's where theirs left it as base held it; where both changed it, this step's only when it is
 * the present's own unsaved value, else theirs. In the present itself, that is: this tab's unsaved
 * edit wins. In an older step, a value this tab saved and theirs changed since takes theirs, so
 * an Undo never brings back what the other save replaced.
 */
function picked<T>(own: T, old: T, their: T, present: T): T {
  if (alike(own, old)) return their;
  if (alike(their, old)) return own;
  return alike(own, present) && !alike(present, old) ? own : their;
}

/** A clip both saves changed, field by field, its placement as one (`picked` for each). */
function mergedClip(mine: Clip, base: Clip, theirs: Clip, present: Clip | undefined): Clip {
  const ours = mine as unknown as Record<string, unknown>;
  const was = base as unknown as Record<string, unknown>;
  const their = theirs as unknown as Record<string, unknown>;
  const now = (present ?? {}) as Record<string, unknown>;
  const part = (fields: Record<string, unknown>) => [...PLACEMENT].map((key) => fields[key]);
  const own = part(ours);
  const placed = picked(own, part(was), part(their), present === undefined ? undefined : part(now)) === own;
  const merged: Record<string, unknown> = { ...ours };
  for (const key of Object.keys(their)) {
    if (PLACEMENT.has(key)) {
      if (!placed) merged[key] = their[key];
    } else {
      merged[key] = picked(ours[key], was[key], their[key], now[key]);
    }
  }
  return merged as unknown as Clip;
}

/**
 * Items by id, merged by `picked` (absent counts as a value: added, or removed), `both` merging an
 * item both saves changed field by field: theirs' order, then this step's own additions.
 */
function mergedById<T extends { id: string }>(
  mine: readonly T[],
  base: readonly T[],
  theirs: readonly T[],
  present: readonly T[],
  both?: (mine: T, base: T, theirs: T, present: T | undefined) => T,
): T[] {
  const ours = new Map(mine.map((item) => [item.id, item]));
  const was = new Map(base.map((item) => [item.id, item]));
  const their = new Map(theirs.map((item) => [item.id, item]));
  const now = new Map(present.map((item) => [item.id, item]));
  const pick = (id: string): T | undefined => {
    const own = ours.get(id);
    const old = was.get(id);
    const next = their.get(id);
    const bothChanged = own !== undefined && old !== undefined && next !== undefined && !alike(own, old) && !alike(next, old);
    return bothChanged && both !== undefined ? both(own, old, next, now.get(id)) : picked(own, old, next, now.get(id));
  };
  const merged: T[] = [];
  for (const id of new Set([...theirs.map((item) => item.id), ...mine.map((item) => item.id)])) {
    const item = pick(id);
    if (item !== undefined) merged.push(item);
  }
  return merged;
}

/** Whether a track's clips, earliest first, lie apart, or back to back from 0 (within 1 ms). */
function laidOut(clips: readonly Clip[], backToBack: boolean): boolean {
  let end = 0;
  for (const clip of clips) {
    if (clip.startMs < end || (backToBack && Math.abs(clip.startMs - end) > 1)) return false;
    end = clip.endMs;
  }
  return true;
}

/** Whether a merged timeline keeps every bound a save holds to. */
function holds(timeline: Timeline): boolean {
  const tracks = new Map(timeline.tracks.map((track) => [track.id, track]));
  if (tracks.size !== timeline.tracks.length || tracks.size > MAX_TRACKS) return false;
  if (timeline.markers.length > MAX_MARKERS || totalMs(timeline) > MAX_TOTAL_MS) return false;
  const counts = new Map<Group, number>();
  for (const clip of timeline.clips) {
    if (tracks.get(clip.trackId)?.kind !== TRACK_OF[clip.kind]) return false;
    counts.set(group(clip.kind), (counts.get(group(clip.kind)) ?? 0) + 1);
  }
  if ([...counts].some(([kind, count]) => count > KIND_LIMIT[kind])) return false;
  // The captions' words: within the timeline's limit together, and each in its caption's span.
  const captions = timeline.clips.filter((clip): clip is CaptionClip => clip.kind === "caption");
  if (!cutFits(captions)) return false;
  const spill = (caption: CaptionClip) =>
    caption.words.some((word) => word.endMs < word.startMs || word.endMs > caption.endMs - caption.startMs + 1);
  if (captions.some(spill)) return false;
  return timeline.tracks.every((track) => laidOut(clipsOn(timeline, track.id), track.magnet));
}

/**
 * Another save merged into this timeline (spec live-sync-merge.md), or why not, by `picked`:
 * `present` is the draft's present, whose unsaved values alone win over theirs; it is this
 * timeline unless the history merges an older or a newer step.
 */
function synced(timeline: Timeline, base: Timeline, theirs: Timeline, present: Timeline): Timeline | string {
  const bothFramed = timeline.aspect !== base.aspect && theirs.aspect !== base.aspect && theirs.aspect !== timeline.aspect;
  if (timeline === present && bothFramed) return REFUSALS.syncConflict();
  const markers = mergedById(timeline.markers, base.markers, theirs.markers, present.markers);
  const merged: Timeline = {
    tracks: picked(timeline.tracks, base.tracks, theirs.tracks, present.tracks),
    clips: mergedById(timeline.clips, base.clips, theirs.clips, present.clips, mergedClip),
    transitions: mergedById(timeline.transitions, base.transitions, theirs.transitions, present.transitions),
    captions: picked(timeline.captions, base.captions, theirs.captions, present.captions),
    aspect: picked(timeline.aspect, base.aspect, theirs.aspect, present.aspect),
    markers: markers.every((marker, at) => marker === timeline.markers[at]) && markers.length === timeline.markers.length
      ? timeline.markers
      : byTime(markers),
  };
  if (!holds(merged)) return REFUSALS.syncConflict();
  return sameParts(timeline, merged) ? timeline : merged;
}

/** Whether two objects hold the same values under the same keys. */
function sameFields(one: object, other: object): boolean {
  const keys = Object.keys(one);
  const values = other as Record<string, unknown>;
  return keys.length === Object.keys(other).length && keys.every((key) => (one as Record<string, unknown>)[key] === values[key]);
}

/** Whether an edit left every part of the timeline as it was: the packing put it back. */
function sameParts(one: Timeline, other: Timeline): boolean {
  const same = <T>(first: readonly T[], second: readonly T[]) =>
    first.length === second.length && first.every((each, at) => each === second[at]);
  return (
    one.aspect === other.aspect &&
    one.captions === other.captions &&
    one.markers === other.markers &&
    same(one.tracks, other.tracks) &&
    same(one.clips, other.clips) &&
    same(one.transitions, other.transitions)
  );
}

/** The timeline with the pasted copies, or why one of them does not fit. */
function pasted(
  timeline: Timeline,
  edit: Extract<TimelineEdit, { type: "paste" }>,
): Timeline | string {
  if (edit.clips.length === 0) return REFUSALS.nothingToPaste();
  // One caption track (spec captions.md decision 8): a caption is not pasted.
  if (edit.clips.some((clip) => clip.kind === "caption")) return REFUSALS.captionCopy();
  const earliest = Math.min(...edit.clips.map((clip) => clip.startMs));
  const at = Math.max(0, whole(edit.atMs));
  let next = timeline;
  for (const [index, clip] of edit.clips.entries()) {
    const id = edit.newIds[index];
    const newTrackId = edit.newTrackIds[index];
    if (id === undefined || newTrackId === undefined) return REFUSALS.pasteIds();
    if (full(next, clip.kind)) return REFUSALS.full(clip.kind);
    const startMs = at + (clip.startMs - earliest);
    const endMs = startMs + (clip.endMs - clip.startMs);
    if (endMs > MAX_TOTAL_MS) return REFUSALS.longest();
    const kind = TRACK_OF[clip.kind];
    const free = (track: Track) =>
      track.kind === kind && !overlaps(next, track.id, id, startMs, endMs);
    const own = next.tracks.find((track) => track.id === clip.trackId);
    // A copy of the magnetic track's clip goes in there by its time (spec magnet-ripple).
    let trackId = own !== undefined && (own.magnet || free(own)) ? own.id : next.tracks.find(free)?.id;
    if (trackId === undefined) {
      if (next.tracks.length >= MAX_TRACKS) return REFUSALS.tracks();
      const track = newTrack(newTrackId, kind);
      next = { ...next, tracks: placeTrack(next.tracks, track) };
      trackId = newTrackId;
    }
    const copy: Clip = { ...clip, id, trackId, startMs, endMs, locked: false };
    next = { ...next, clips: [...next.clips, copy] };
  }
  return next;
}

function withTransition(timeline: Timeline, changed: Transition): Timeline {
  return {
    ...timeline,
    transitions: timeline.transitions.map((each) => (each.id === changed.id ? changed : each)),
  };
}

/** A cut of a video track: a picture and the next, which starts at most 0.1 s after it ends. */
export interface Junction {
  fromId: string;
  toId: string;
  trackId: string;
  /** The cut's time: the middle of the gap, if there is one. */
  atMs: number;
  /** The longest a transition here lasts: the shorter picture's length. */
  limitMs: number;
  transition: Transition | null;
}

/** Every cut of the timeline, track by track, earliest first, with its transition if it has one. */
export function junctionsOf(timeline: Timeline): Junction[] {
  const junctions: Junction[] = [];
  for (const track of timeline.tracks) {
    if (track.kind !== "video") continue;
    const clips = clipsOn(timeline, track.id);
    for (const [at, from] of clips.entries()) {
      const to = clips[at + 1];
      if (to === undefined || to.startMs - from.endMs > JUNCTION_GAP_MS) continue;
      junctions.push({
        fromId: from.id,
        toId: to.id,
        trackId: track.id,
        atMs: (from.endMs + to.startMs) / 2,
        limitMs: Math.min(clipMs(from), clipMs(to)),
        transition:
          timeline.transitions.find((each) => each.fromId === from.id && each.toId === to.id) ??
          null,
      });
    }
  }
  return junctions;
}

/** Live's tile click: the cut nearest the playhead, or none. */
export function nearestJunction(timeline: Timeline, atMs: number): Junction | null {
  let nearest: Junction | null = null;
  for (const junction of junctionsOf(timeline)) {
    if (nearest === null || Math.abs(junction.atMs - atMs) < Math.abs(nearest.atMs - atMs)) {
      nearest = junction;
    }
  }
  return nearest;
}

/**
 * The transitions kept after an edit: each on a cut still (a clip moved, trimmed or removed
 * away from its cut takes its transition with it), no longer than its shorter picture.
 */
function onCuts(timeline: Timeline): Timeline {
  if (timeline.transitions.length === 0) return timeline;
  const cuts = new Map(
    junctionsOf(timeline).flatMap((cut) => (cut.transition === null ? [] : [[cut.transition.id, cut]])),
  );
  let changed = false;
  const transitions = timeline.transitions.flatMap((each) => {
    const cut = cuts.get(each.id);
    if (cut === undefined) {
      changed = true;
      return [];
    }
    if (each.durationMs <= cut.limitMs) return [each];
    changed = true;
    return [{ ...each, durationMs: cut.limitMs }];
  });
  return changed ? { ...timeline, transitions } : timeline;
}

const LEVEL = { volume: 1, volumePoints: [] as readonly VolumePoint[], fadeInMs: 0, fadeOutMs: 0 } as const;
/** A video or a sound with no speed curve (spec clip-speed-curves); as it is added, at 1×. */
const NO_CURVE = { speedPoints: [] as readonly SpeedPoint[], speedCurve: null } as const;
const ONE_SPEED = { speed: 1, ...NO_CURVE } as const;

/** A clip's new animations, within the contract's count; a text keeps no combo, a picture no
 * text preset. Transitions and Custom are every clip's. */
function animated(clip: VideoClip | ImageClip | TextClip, next: readonly Animation[] | undefined): readonly Animation[] {
  if (next === undefined) return clip.animations;
  const taken = next.filter((each) => {
    const group = PRESETS[each.preset].group;
    return group === "transition" || group === "custom" || (group === "text") === (clip.kind === "text");
  });
  return taken.slice(0, MAX_ANIMATIONS);
}

function patched(clip: Clip, patch: ClipPatch): Clip {
  const length = clip.endMs - clip.startMs;
  const hidden = clip.kind === "audio" ? false : (patch.hidden ?? clip.hidden);
  const fade = (ms: number | undefined, current: number) =>
    ms === undefined ? current : clamp(whole(ms), 0, Math.min(MAX_FADE_MS, length));
  switch (clip.kind) {
    case "video":
      return {
        ...clip,
        ...looked(clip, patch),
        animations: animated(clip, patch.animations),
        hidden,
        muted: patch.muted ?? clip.muted,
        volume: clamp(patch.volume ?? clip.volume, 0, MAX_VOLUME),
        fadeInMs: fade(patch.fadeInMs, clip.fadeInMs),
        fadeOutMs: fade(patch.fadeOutMs, clip.fadeOutMs),
      };
    case "audio":
      return {
        ...clip,
        muted: patch.muted ?? clip.muted,
        volume: clamp(patch.volume ?? clip.volume, 0, MAX_VOLUME),
        fadeInMs: fade(patch.fadeInMs, clip.fadeInMs),
        fadeOutMs: fade(patch.fadeOutMs, clip.fadeOutMs),
      };
    case "text":
      return {
        ...clip,
        hidden,
        text: patch.text ?? clip.text,
        style: patch.style === undefined ? clip.style : styled(clip.style, patch.style),
        animations: animated(clip, patch.animations),
      };
    case "image":
      return { ...clip, ...looked(clip, patch), animations: animated(clip, patch.animations), hidden };
    case "effect":
      return {
        ...clip,
        hidden,
        values: patch.effectValues === undefined ? clip.values : { ...clip.values, ...patch.effectValues },
      };
    case "caption":
      // The canvas's arrows and drags move a caption's box (live's pixels), as a picture's,
      // held to the contract's box however far it goes; its handles scale and turn it, each held
      // to the contract's range (#560).
      return {
        ...clip,
        hidden,
        box: patch.box == null ? clip.box : boxWithin(patch.box),
        scale: clamp(finite(patch.scale, clip.scale), MIN_CAPTION_SCALE, MAX_CAPTION_SCALE),
        rotation: clamp(Math.round(finite(patch.rotation, clip.rotation) * 10) / 10, -360, 360),
      };
  }
}

/** A caption's words kept to its length (the contract's bound), each in order. */
function wordsWithin(words: readonly CaptionWord[], lengthMs: number): readonly CaptionWord[] {
  let before = 0;
  return words.map((word) => {
    const startMs = clamp(whole(word.startMs), before, lengthMs);
    before = startMs;
    return { ...word, startMs, endMs: clamp(whole(word.endMs), startMs, lengthMs) };
  });
}

/**
 * A caption cut to run from `startMs` to `endMs` on the video, its words where they were in time
 * (spec captions.md decision 4): the ones wholly outside dropped, the others timed from the new
 * start and kept inside it. A trim keeps a word its start cuts through; a split's second part
 * (`fromCut`) does not, as the word on the cut stays with the part it starts in.
 */
function captionFrom(clip: CaptionClip, startMs: number, endMs: number, fromCut = false): CaptionClip {
  const shift = startMs - clip.startMs;
  const lengthMs = endMs - startMs;
  const kept = clip.words
    .map((word) => ({ ...word, startMs: word.startMs - shift, endMs: word.endMs - shift }))
    .filter((word) => word.startMs < lengthMs && (fromCut ? word.startMs >= 0 : word.endMs > 0 || word.startMs >= 0));
  const first = kept[0]?.line ?? 0;
  const lined = kept.map((word) => ({ ...word, line: word.line - first }));
  return { ...clip, startMs, endMs, words: wordsWithin(lined, lengthMs) };
}

/** Live's import: every caption replaced by `captions`, on the caption track; or why not. */
function withCaptions(timeline: Timeline, captions: readonly NewCaption[], newTrackId: string): Timeline | string {
  const [next, trackId] = trackFor(timeline, "caption", newTrackId);
  if (trackId === "") return REFUSALS.tracks();
  const kept = next.clips.filter((clip) => clip.kind !== "caption");
  // Never more than the contract holds: a list past it changes nothing.
  if (!cutFits(captions)) return REFUSALS.captionCount();
  const added: CaptionClip[] = captions.map((caption) => {
    const startMs = clamp(whole(caption.startMs), 0, MAX_TOTAL_MS - 1);
    const endMs = clamp(whole(caption.endMs), startMs + 1, MAX_TOTAL_MS);
    return {
      id: caption.id,
      trackId,
      kind: "caption",
      startMs,
      endMs,
      locked: false,
      hidden: caption.hidden ?? false,
      words: wordsWithin(caption.words, endMs - startMs),
      box: boxWithin(caption.box),
      // A new cut starts upright at the style's size, as live's re-made captions do.
      scale: 1,
      rotation: 0,
    };
  });
  return { ...next, clips: [...kept, ...added] };
}

/** Every caption's words on the video's time, in order. */
export function spokenWords(timeline: Timeline): TimedWord[] {
  return timeline.clips
    .filter((clip): clip is CaptionClip => clip.kind === "caption")
    .sort((one, other) => one.startMs - other.startMs)
    .flatMap((caption) =>
      caption.words.map((word) => ({
        text: word.text,
        startMs: caption.startMs + word.startMs,
        endMs: caption.startMs + word.endMs,
        emphasis: word.emphasis,
      })),
    );
}

/** Whether a cut is within the contract's counts: 1000 captions, 5000 words. */
/**
 * A caption's box held to the contract's `Box`: its corner within ±10000, its size from 1 to
 * 10000. Live's largest size, line height and lines make a box past that, and so can a shift of
 * one; the words then reach past their box rather than the save being refused.
 */
function boxWithin(box: CaptionBox): CaptionBox {
  return {
    x: clamp(box.x, -MAX_PLACE, MAX_PLACE),
    y: clamp(box.y, -MAX_PLACE, MAX_PLACE),
    width: clamp(box.width, 1, MAX_PLACE),
    height: clamp(box.height, 1, MAX_PLACE),
  };
}

export function cutFits(cut: readonly { words: readonly unknown[] }[]): boolean {
  const words = cut.reduce((sum, caption) => sum + caption.words.length, 0);
  return cut.length <= MAX_CAPTIONS && words <= MAX_TIMELINE_CAPTION_WORDS;
}

/**
 * Every caption cut again by `settings` from the captions' own words (spec captions.md decision
 * 3), each new one hidden where the caption its start fell in was; refused while a caption is
 * locked (a lock keeps it as it is) or when the cut would not fit.
 */
function recutCaptions(timeline: Timeline, settings: CaptionSettings, cut: CaptionCut, idPrefix: string): NewCaption[] | string {
  const captions = timeline.clips.filter((clip): clip is CaptionClip => clip.kind === "caption");
  const locked = captions.find((caption) => caption.locked);
  if (locked !== undefined) return REFUSALS.locked(locked.id);
  const segments = segmentWords(spokenWords(timeline), settings.paragraph, cut.frame, cut.metrics, settings.placement);
  if (!cutFits(segments)) return REFUSALS.captionCount();
  return segments.map((segment, at) => ({
    ...segment,
    id: `${idPrefix}-${at}`,
    hidden: captions.find((caption) => showsAt(caption, segment.startMs))?.hidden ?? false,
  }));
}


/** A clip's length on the video. */
export function clipMs(clip: Clip): number {
  return clip.endMs - clip.startMs;
}

/** The video's length: until its last clip ends. */
export function totalMs(timeline: Timeline): number {
  return Math.max(0, ...timeline.clips.map((clip) => clip.endMs));
}

/** Whether a clip shows at a time: a time on a boundary belongs to the clip that starts there. */
export function showsAt(clip: Clip, atMs: number): boolean {
  return atMs >= clip.startMs && atMs < clip.endMs;
}

/**
 * Where a sound's beat, a time in its file, plays on the video (spec beats.md): the one mapping of
 * a beat to the timeline, through its speed (spec clip-speed); `readsAt` is its inverse.
 */
export function beatAt(clip: AudioClip, beatMs: number): number {
  return Math.round(clipMsAt(clip, beatMs));
}

/**
 * The span of a sound's file its beats are found over (spec find-beats.md): what it reads, and as
 * far as a trim of it can reach either way (a clip lasts at most `MAX_TOTAL_MS` of the video, so
 * that times its speed of its file), within the file and `MAX_FILE_MS`.
 */
export function beatReach(clip: AudioClip, sourceMs: number | null): { fromMs: number; toMs: number } {
  return {
    fromMs: Math.max(0, Math.round(fileMsAt(clip, clip.endMs - MAX_TOTAL_MS))),
    toMs: Math.min(sourceMs ?? MAX_FILE_MS, Math.round(fileMsAt(clip, clip.startMs + MAX_TOTAL_MS)), MAX_FILE_MS),
  };
}

/** A sound's beats that show, on the video's time: those inside the span of its file it reads. */
export function beatTimes(clip: AudioClip): number[] {
  return clip.beatsMs.map((beat) => beatAt(clip, beat)).filter((atMs) => showsAt(clip, atMs));
}

/** The beat that shows nearest the video's `atMs`, within `BEAT_REACH_MS`, or null. */
function nearestBeat(clip: AudioClip, atMs: number): number | null {
  let near: number | null = null;
  let by = BEAT_REACH_MS;
  for (const beat of clip.beatsMs) {
    const at = beatAt(clip, beat);
    if (showsAt(clip, at) && Math.abs(at - atMs) <= by) {
      near = beat;
      by = Math.abs(at - atMs);
    }
  }
  return near;
}

/** A sound's beats as the contract keeps them: whole, within `MAX_FILE_MS`, rising, none twice. */
function beatList(beats: readonly number[]): number[] {
  const held = beats.map((beat) => clamp(whole(beat), 0, MAX_FILE_MS));
  return [...new Set(held)].sort((one, other) => one - other);
}

/**
 * The beats a sound keeps of those found, at most `MAX_BEATS` (spec find-beats.md, decision 6):
 * all while they fit; else every one in the span of its file it plays, then those nearest that span
 * either way. Past the limit within that span alone, every second (or third...) one of it: a grid at
 * half (or a third of) the tempo still lies on the beat, from the clip's start to its end.
 */
function beatsKept(clip: AudioClip, beats: readonly number[]): number[] {
  const found = beatList(beats);
  if (found.length <= MAX_BEATS) return found;
  const fromMs = clip.inMs;
  const toMs = readsAt(clip, clip.endMs);
  const played = found.filter((beat) => beat >= fromMs && beat < toMs);
  if (played.length > MAX_BEATS) {
    const step = Math.ceil(played.length / MAX_BEATS);
    return played.filter((_, at) => at % step === 0);
  }
  const off = (beat: number) => (beat < fromMs ? fromMs - beat : beat - toMs);
  const nearest = found
    .filter((beat) => beat < fromMs || beat >= toMs)
    .sort((one, other) => off(one) - off(other))
    .slice(0, MAX_BEATS - played.length);
  return [...played, ...nearest].sort((one, other) => one - other);
}

/** The picture in front at a time: the video or still on the highest track that has one. */
export function frontAt(
  timeline: Timeline,
  atMs: number,
): { clip: VideoClip | ImageClip; sourceMs: number } | null {
  for (const track of timeline.tracks) {
    if (track.kind !== "video") continue;
    const clip = clipsOn(timeline, track.id).find((each) => showsAt(each, atMs));
    if (clip !== undefined && (clip.kind === "video" || clip.kind === "image")) {
      const sourceMs = clip.kind === "video" ? readsAt(clip, atMs) : 0;
      return { clip, sourceMs };
    }
  }
  return null;
}

/** The titles showing at a time, top track first. */
export function titlesAt(timeline: Timeline, atMs: number): TextClip[] {
  return timeline.tracks
    .filter((track) => track.kind === "text")
    .flatMap((track) => clipsOn(timeline, track.id))
    .filter((clip): clip is TextClip => clip.kind === "text" && showsAt(clip, atMs));
}

/** The sounds playing at a time (unmuted, on unmuted tracks), with their source time. */
export function soundsAt(timeline: Timeline, atMs: number): { clip: AudioClip; sourceMs: number }[] {
  const muted = new Set(timeline.tracks.filter((track) => track.muted).map((track) => track.id));
  return timeline.clips
    .filter(
      (clip): clip is AudioClip =>
        clip.kind === "audio" && !clip.muted && !muted.has(clip.trackId),
    )
    .filter((clip) => showsAt(clip, atMs))
    .map((clip) => ({ clip, sourceMs: readsAt(clip, atMs) }));
}

/** The clips with a media source, for the bin's lookups. */
export function mediaClips(timeline: Timeline): MediaClip[] {
  return timeline.clips.filter(
    (clip): clip is MediaClip => clip.kind === "video" || clip.kind === "image" || clip.kind === "audio",
  );
}

/** The tracks of a kind, top first, for names like "Video track 2". */
export function trackName(timeline: Timeline, track: Track): string {
  if (track.kind === "caption") return "Captions";
  const word = { video: "Video", audio: "Audio", text: "Text", effect: "Effect" }[track.kind];
  const same = timeline.tracks.filter((each) => each.kind === track.kind);
  const number = same.indexOf(track) + 1;
  return same.length > 1 ? `${word} track ${number}` : `${word} track`;
}

/** How many clips of a kind's group the timeline holds. */
export function countOf(timeline: Timeline, kind: Clip["kind"]): number {
  const g = group(kind);
  return timeline.clips.filter((clip) => group(clip.kind) === g).length;
}

/** The media kinds the project holds the most of: none of them can be added. */
export function fullKinds(timeline: Timeline): ReadonlySet<MediaKind> {
  const kinds: MediaKind[] = ["video", "image", "audio"];
  return new Set(kinds.filter((kind) => full(timeline, kind)));
}

/** A new id for a clip or a track: the contract takes letters, digits, `_` and `-`. */
export function newId(): string {
  return crypto.randomUUID();
}
